import React, { useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { useQueryClient } from '@tanstack/react-query';
import {
  AppContext,
  SnackContext,
  ThemeContext,
  UserContext
} from 'src/store/ContextStore';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';
import ChartBox from './ChartBox';
import { createAutomaticWriteLatch } from '../workspace/editSignalPolicy';
import {
  createAutosaveController,
  runWithAutosaveHeld
} from './autosaveController';
import { MemberRowSyncContext } from './memberRowSyncContext';
import { useExternalMemberRowRefresh } from './useExternalMemberRowRefresh';
import CascadeScreeningButton from './CascadeScreeningButton';
import HeaderTitle from 'src/components/UI/header/HeaderTitle';
import PedigreeDates from './canvas-tools/PedigreeDates';
import FhqReviewBanner from './canvas-tools/FhqReviewBanner';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import LegendMenu from '../pedigree-sidebar/sections/LegendMenu';
import { applyColourKey } from './carrier-status/applyColourKey';
import { useDiseaseColourKey } from './carrier-status/useDiseaseColourKey';
import { useCarrierStatus } from './carrier-status/useCarrierStatus';
import {
  withFetusDiseases,
  fetusDiagnosisIds
} from './pedigree/fetusDiagnoses';
import { resolveCarrierStyle } from './carrier-status/carrierStyle';
import { usePedigreeAnalysis } from './analysis/usePedigreeAnalysis';
import { validatePedigreeConfig } from './pedigree/utils';
import { pruneRanksForStructuralEdit } from './pedigree/structuralEditOrder';
import {
  StructuralEditContext,
  applyStructuralEditPrune
} from './structuralEditContext';
import {
  applyIdentityReconciliation,
  mintObjectIdHex
} from './pedigree/saveReconciliation';
import {
  materialisePlaceholders,
  placeholderSignature,
  hasUnsavedMaterialised,
  decidePlaceholderAutoSave
} from './pedigree/materialisePlaceholders';
import { resolveConf } from './pedigree/configLookup';
import { formatMemberConsultationDates } from './pedigree/consultationDates';
import {
  useLayoutHistory,
  pinMapsEqual,
  orderMapsEqual,
  snapshotLayoutState
} from './useLayoutHistory';
import ViewsMenu from './canvas-tools/ViewsMenu';
import { config as layoutConfig } from './pedigree/config';
import { APICONSTANTS, CONSTANTS, PEDIGREE } from 'src/components/Constants';
import { EVENTS, getUniqueDiagnoses } from 'src/components/admin/appReducer';
import axios from 'src/axios';
import {
  PeopleAlt,
  PlaylistAdd,
  UnfoldLess,
  UnfoldMore,
  ExpandMore,
  ExpandLess,
  History,
  Label as LabelIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { getProbandState } from './pedigree/probandState';
import OverlaysMenu from './canvas-tools/OverlaysMenu';
import useDataOverlays from './data-overlays/useDataOverlays';
import { useCopilot } from 'src/components/VirtualAssistant/copilot/CopilotContext';
import { usePedigreeInstance } from './pedigree/instanceContext';
import DataOverlayResultsBar from './data-overlays/DataOverlayResultsBar';
import DataOverlayLegend from './data-overlays/DataOverlayLegend';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModificationHistoryModal from 'src/components/pedigree-chart/pedigree-sidebar/ModificationHistoryModal';
import LabelPresetModal from '../label-modal/LabelPresetModal';
import LegendConfigModal from '../label-modal/LegendConfigModal';
import GenotypesLegend from '../pedigree-sidebar/sections/GenotypesLegend';
import PhenotypesLegend from '../pedigree-sidebar/sections/PhenotypesLegend';
import {
  legendDragAllowed,
  makeLegendDropBlocker
} from '../pedigree-sidebar/sections/legendDrag/legendDropGate';
import VariantPathogenicityLegend from '../pedigree-sidebar/sections/VariantPathogenicityLegend';
import PedigreeAIGenerator, {
  applyPedigreeConfig,
  showSketchLauncher
} from '../pedigree-ai-generator';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import UnrecognisedCarrierStatusNotice from '../UnrecognisedCarrierStatusNotice';
import trakgeneLogo from 'src/assets/images/trakgeneLogo.png';
import { isAnonymousActive, isAnonymousOrLoading } from '../anonymousPedigrees';
import usePersonLinkBatch from 'src/hooks/usePersonLinkBatch';
import useThemeAssetRefresh from 'src/hooks/useThemeAssetRefresh';
import {
  linkedRecordPresence,
  LINK_GLYPH_PATH
} from './linked-records/linkedRecordBadge';

// ---------------------------------------------------------------------------
// THE AUTOSAVE MUST NEVER REPORT A WRITE IT DID NOT SEND.
//
// THE DEFECT THESE EXIST FOR. `onSumbit` below is the ONLY code path that
// writes member records to the server; every sidebar section and node menu
// "saves" by mutating `memberRecords` and setting save -> IN_PROGRESS, and the
// debounced scheduler turns that into one POST. Two ways that promise was
// broken:
//
//   1. It opened with `if (isLocked) return Promise.resolve()`. `isLocked` means
//      ANOTHER user holds the family lock, and a person tab makes the sidebar
//      editable in exactly that state (a person lock overrides a family lock for
//      that patient's own fields). So PersonMenu snacked "Saved", the records
//      changed in memory, the POST never fired, and the edit died with the tab.
//      Silent clinical data loss.
//   2. The `finally` flipped the status to SAVED whatever happened, so a
//      REFUSED write — the 409 the backend now returns for a pedigree carrying
//      another family's members, a 413, a 500, a dropped connection — ended with
//      a green tick.
//
// Both are fixed by routing every attempt through these two pure functions: the
// status only reaches SAVED when a 2xx came back, and any other outcome names
// itself. Exported (and pure) so the rule is pinned by tests instead of only
// being reachable by mounting this 2600-line component.
//
// SAVE_NOT_SENT deliberately does NOT live in PEDIGREE.saveStatus. Every other
// module writes that enum and only ever writes IN_PROGRESS; this is a value
// nothing outside this file may set, and `=== SAVED` / `=== IN_PROGRESS` both
// read false for it, which is exactly the behaviour the rest of the chart wants
// (buttons re-enable, no "Saved just now" flash, the next edit re-arms a save).
export const SAVE_NOT_SENT = 'Not saved';

/**
 * What an autosave attempt should do before it touches the network.
 *
 * @param {boolean} [automatic=false] - the pending status was armed by the
 *   chart itself (TGCLOUD-1813: the no-config fallback committing the members
 *   it materialised from placeholders), not by a user gesture.
 * @param {boolean} [rowRefreshPending=false] - a member row is known to be
 *   stale and its refresh from the server has not landed (an autosave hold,
 *   autosaveController.js).
 * @returns {{send: boolean, nextSave: string|null, blockedMessage: string|null,
 *   deferred?: boolean}}
 *   `nextSave` is the status to move to when nothing is sent (null = leave it
 *   alone, which is the "already saved, nothing to do" case). `deferred` marks
 *   an attempt that was put off, not decided: the release of the hold re-arms
 *   the scheduler, and that attempt decides.
 */
export const planSaveAttempt = ({
  familyLockedByOther,
  alreadySaved,
  recordCount,
  preview,
  automatic = false,
  rowRefreshPending = false
}) => {
  // ORDER IS LOAD-BEARING: "is there anything to lose?" is answered BEFORE "may
  // we write?".
  //
  // The scheduler that calls this is armed by a bare
  // `useEffect(..., [memberRecords])`, so it runs on MOUNT and again on every
  // re-set of memberRecords the user did not cause — the hydration effect, and
  // ChartBox's render-time proxy-id pruning. When the lock test came first,
  // merely OPENING a pedigree another clinician holds raised a red "your change
  // was not saved" snackbar over an edit that never existed, and latched the
  // indicator to SAVE_NOT_SENT for the rest of the session. That trains people
  // to ignore the one indicator that means real data was lost, which is the
  // defect the refusal branch exists to prevent.
  //
  // `alreadySaved` is the whole test for "nothing pending": the status starts at
  // SAVED and only a real edit moves it to IN_PROGRESS.
  if (alreadySaved) {
    return { send: false, nextSave: null, blockedMessage: null };
  }
  // Nothing to write: an empty family is a legitimately saved state.
  if (!recordCount) {
    return {
      send: false,
      nextSave: PEDIGREE.saveStatus.SAVED,
      blockedMessage: null
    };
  }
  // A read-only preview (FHQ / SMART modal) reaches here with the same
  // `familyLockedByOther` flag a real foreign lock sets — PedigreeChartView
  // hard-codes isLocked=true for preview. It must stay SILENT: there is no other
  // user, so "another user is editing" would be a plain untruth, and there is no
  // edit surface, so nothing can be pending to lose.
  if (preview) {
    return { send: false, nextSave: null, blockedMessage: null };
  }
  // A row in this chart is known to be stale: another endpoint has changed it
  // on the server (variants pushed from a Test record, or a person-link sync
  // made from another tab) and the server's answer is not on the row yet. The POST carries every member's whole row, so
  // sending now would write the old one back over that change. The attempt is
  // put off, not refused: the status stays where it is, nothing is reported,
  // and releasing the hold re-arms the scheduler, so the pending edit is sent
  // once — from the refreshed rows. Deferring the whole save rather than
  // dropping the stale row from it is deliberate; see autosaveController.js.
  if (rowRefreshPending) {
    return {
      send: false,
      nextSave: null,
      blockedMessage: null,
      deferred: true
    };
  }
  // Another clinician holds the family lock, and there IS an unsent edit. There
  // is no write we can make that would not overwrite theirs — the endpoint takes
  // the WHOLE member array — so the save is refused here rather than
  // half-attempted, and the user is told in plain words instead of being shown a
  // tick.
  if (familyLockedByOther) {
    // A save NO USER INITIATED must never raise the not-saved alarm
    // (TGCLOUD-1813). The fallback arms its automatic save only after the lock
    // has been read as free, but the lock can change hands inside the 300 ms
    // debounce; telling the clinician "your change was not saved" over a
    // change they never made is exactly the false alarm the ordering above
    // exists to prevent. Nothing is lost: the materialised member stays in
    // memberRecords and rides along with the first real edit, whose refusal —
    // if the lock is still foreign — DOES get the message.
    if (automatic) {
      return {
        send: false,
        nextSave: PEDIGREE.saveStatus.SAVED,
        blockedMessage: null
      };
    }
    return {
      send: false,
      nextSave: SAVE_NOT_SENT,
      blockedMessage:
        'This pedigree is being edited by another user, so your change was not saved. Ask them to release the editing lock, or request it from the banner at the bottom of the screen.'
    };
  }
  return { send: true, nextSave: null, blockedMessage: null };
};

/**
 * How to report a save the server refused. The 409 is the foreign-member gate
 * (backend/routes/pedigree.js) and carries a specific, actionable message —
 * "contains people belonging to another family... Reload the pedigree and try
 * again" — which was being swallowed and replaced with "An error occured".
 */
export const describeSaveFailure = (err) => {
  const status = err?.response?.status ?? err?.status;
  if (status === 413) {
    return {
      severity: 'warning',
      msg: translate('Pedigree Maximum members has exceeded')
    };
  }
  if (status === 409) {
    return {
      severity: 'error',
      msg:
        err?.response?.data?.error ||
        translate(
          'This pedigree contains people belonging to another family, so it was not saved. Reload the pedigree and try again.'
        )
    };
  }
  // 400 is the placeholder gate (TGCLOUD-1813): the server refuses a node with
  // a non-numeric pid or isDummy before it mints a UPN or writes a row, and
  // says what to do about it. Show that, not the generic line.
  if (status === 400) {
    return {
      severity: 'error',
      msg: err?.response?.data?.error || translate('An error occured')
    };
  }
  return { severity: 'error', msg: translate('An error occured') };
};

// Legend swatch mirroring the on-node record-status badge: FILLED teal = primary,
// OUTLINE teal = secondary. Shared by the on-screen and export legends so the two
// never diverge. Only rendered when the role is actually present on the pedigree
// (see the callers) so an empty pedigree shows no record legend.
function RecordBadgeSwatch({ role, teal, label }) {
  const isPrimary = role === 'primary';
  const scale = 11 / 24; // 24-unit MUI glyph into an ~11px swatch glyph
  const offset = 9 - 12 * scale; // centre the glyph in the 18x18 box
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
      <svg width="18" height="18" style={{ flexShrink: 0, marginRight: 4 }}>
        <rect
          x="1"
          y="1"
          width="16"
          height="16"
          rx="3"
          fill={isPrimary ? teal : '#ffffff'}
          stroke={isPrimary ? 'none' : teal}
          strokeWidth={isPrimary ? 0 : 1.5}
        />
        <path
          d={LINK_GLYPH_PATH}
          transform={`translate(${offset}, ${offset}) scale(${scale})`}
          fill={isPrimary ? '#ffffff' : teal}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

// Width of the fixed top-right overlay column. The legend/family card and the
// FHQ review banner stack as one column at the same x-position, so BOTH must use
// this — hardcoding either one alone leaves the banner visibly stepped in.
//
// Sized off the header row, which is the constraint: the icon strip is
// flexShrink: 0 and takes up to 7 * 30px, leaving `width - 32px padding - 210px`
// for the G number. At the old 350 that was 108px, and a G number renders at
// (Inter 700, 1.2rem) 100.7px for `G-1-00001` but 117.4px for a two-digit user
// group — so `G-99-00001` ellipsised (TGCLOUD-1730). 376 gives 134px, which
// clears two- and three-digit user groups. Four digits still ellipsises, and the
// Typography keeps its `title` so the full value is available on hover.
const RIGHT_PANEL_WIDTH = 376;

const RightContainer = styled(Grid)(({ theme }) => ({
  position: 'fixed',
  right: 28,
  top: 80,
  zIndex: 9,
  width: RIGHT_PANEL_WIDTH,
  maxHeight: 'calc(100vh - 80px)',
  display: 'none',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  padding: '12px 16px',
  gap: 12,
  [theme.breakpoints.up('sm')]: {
    display: 'flex'
  }
}));

// Carrier projection used by the canvas border + legends when the carrier
// toggle is off — i.e. no carrier marks rendered.
const EMPTY_CARRIER = Object.freeze({
  byMember: {},
  presentStatesByDisease: {},
  diseaseColorMap: {}
});

// The contexts PedigreeChart provides to ChartBox and everything under it —
// the canvas menus, the sidebar, the person region and the bulk editor. One
// wrapper rather than two nested providers in the render below.
function ChartContexts({ structuralEdit, memberRowSync, children }) {
  return (
    <StructuralEditContext.Provider value={structuralEdit}>
      <MemberRowSyncContext.Provider value={memberRowSync}>
        {children}
      </MemberRowSyncContext.Provider>
    </StructuralEditContext.Provider>
  );
}

function Pedigree({
  // TGCLOUD-1669 Phase 7: this instance is a PERSON tab. The whole data,
  // persistence and lock chain below is reused unchanged; only the canvas is
  // suppressed and this member is selected on arrival.
  personMemberId = null,
  initialViewState,
  onPersonLockStateChange,
  // Pass-through to the sidebar, for the tab strip's close guard
  // (TGCLOUD-1839). See PedigreeChartView.
  onUnsavedSectionsChange,
  pedigreeConfig,
  // True once the pedigree-configuration fetch has RESOLVED (success or error). Member
  // mapping waits for it: pedigreeConfig starts null while the fetch is in flight, and
  // mapping members against the fallback first (a) fired the "No pedigree configuration
  // detected" warning on every open of a CONFIGURED family, (b) dispatched throwaway
  // layout computes for the unconfigured members, and (c) could paint the unconfigured
  // layout briefly before the real one replaced it — a visible rearrangement on open.
  configLoaded,
  // True when that fetch FAILED, false when it returned (including a genuine
  // `configuration: null`). The two used to be indistinguishable here
  // (TGCLOUD-1804), and the fallback branch below now needs to tell them apart:
  // it may auto-save the members it materialised from placeholders only when
  // the server said there is nothing stored — never over a configuration it
  // simply could not read (TGCLOUD-1813).
  configReadFailed = false,
  isMigrated,
  options,
  setOptions,
  labelsSet,
  setLabelsSet,
  labelPresets,
  reloadPresets,
  appliedPresetId,
  setAppliedPresetId,
  patient,
  position,
  initialOrder,
  initialOrderLocked = false,
  isLocked,
  // True once usePedigreeLock has READ the family lock for this family (the
  // first acquire/GET SUCCEEDED — a failed read leaves it false, because a
  // lockState that is null-because-unknown reads downstream exactly like
  // null-because-free); true throughout in preview.
  // `isLocked` is false on first render whoever holds the lock, so the
  // fallback's automatic save (TGCLOUD-1813) must not decide on it until this
  // is true. Defaults to false, which is the safe direction: an unknown lock
  // state never auto-saves, and the user's first edit saves as before.
  lockResolved = false,
  isMine,
  lockState,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  personLockHook,
  initialDateModified,
  // Fired the first time the user edits this chart (save → IN_PROGRESS). The
  // workspace uses it to lazily acquire the edit lock + auto-pin the tab; unused
  // (no-op) in the classic single-pedigree flow. See usePedigreeLock.
  onEdit,
  // Read-only FHQ preview (rendered inside FhqPreviewModal). Suppresses the
  // viewport-anchored family card and the FHQ review banner, which are
  // position:fixed and would otherwise paint over the page outside the dialog.
  preview = false
}) {
  const [sidebarState, setSidebarState] = React.useState({
    open: false,
    expand: false
  });
  const [legendCollapsed, setLegendCollapsed] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [labelModalOpen, setLabelModalOpen] = React.useState(false);
  const [legendModalOpen, setLegendModalOpen] = React.useState(false);
  const [hiddenPanelOpen, setHiddenPanelOpen] = React.useState(false);
  // Bumped by the V shortcut, consumed by OverlaysMenu (TGCLOUD-1670).
  const [overlaysOpenSignal, setOverlaysOpenSignal] = React.useState(0);
  const [selectedHiddenPids, setSelectedHiddenPids] = React.useState([]);
  const pedRef = React.useRef();
  const chartBoxRef = useRef();

  const familyCardRef = React.useRef(null);
  const [familyCardBottom, setFamilyCardBottom] = React.useState(null);
  React.useEffect(() => {
    const el = familyCardRef.current;
    if (!el || preview) return undefined;
    const measure = () =>
      setFamilyCardBottom(el.getBoundingClientRect().bottom);
    measure();
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [preview]);

  // SAVED VIEWS routing refs (Phase 2) — declared up here because the three
  // prop-resync effects below must consult them: activeViewRef is the ACTIVE
  // SOURCE (null = Standard), standardStashRef holds the standard layout
  // triple while a view is active. The rest of the views machinery lives
  // after the autosave effects.
  const activeViewRef = React.useRef(null);
  const standardStashRef = React.useRef(null);
  // postPedigreeConfig (the Standard doc's serialised POST), for callers declared
  // above it — the structural-edit prune posts the stash through it.
  const postStandardConfigRef = React.useRef(null);
  const mergeStandardStash = (patch) => {
    const cur = standardStashRef.current || {};
    standardStashRef.current = {
      customPositions:
        cur.customPositions instanceof Map ? cur.customPositions : new Map(),
      establishedOrder:
        cur.establishedOrder instanceof Map ? cur.establishedOrder : new Map(),
      orderLocked: !!cur.orderLocked,
      ...patch
    };
  };

  const t = useAutoTranslation();

  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user, family } = users;
  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);
  // The export logo's URL is signed and expires; the theme was fetched at
  // sign-in. Re-signed if it has lapsed by the time a pedigree is opened.
  const refreshThemeAssets = useThemeAssetRefresh();
  const { user: loggedInUser } = React.useContext(UserContext);
  // The security-group half of enforce('pedigrees', 'write'), for the sketch launcher.
  const { canWrite: canWriteModule } = React.useContext(SgAccessContext);
  const queryClient = useQueryClient();

  // PEDIGREE DATA
  const [memberRecords, setMemberRecords] = React.useState([]);
  const memberRecordsRef = React.useRef(memberRecords);
  const hasPendingUpnUpdates = React.useRef(false);
  const [familyData, setFamilyData] = React.useState(null);
  const [customPositions, setCustomPositions] = React.useState(
    position instanceof Map ? position : new Map(Object.entries(position || {}))
  );
  // true on initial load or position prop update; added to resovle api firing on pedigire open
  const isExternalPositionUpdate = React.useRef(true);

  React.useEffect(() => {
    if (position && position.size > 0) {
      const next =
        position instanceof Map
          ? position
          : new Map(Object.entries(position || {}));
      if (activeViewRef.current) {
        // A late-arriving standard config while a VIEW is active belongs in
        // the switch-back stash, never on screen: resyncing live state would
        // clobber the applied view and the engine's settled auto-capture
        // would then rewrite the view doc with standard-derived layout.
        mergeStandardStash({ customPositions: new Map(next) });
        return;
      }
      isExternalPositionUpdate.current = true;
      setCustomPositions(next);
    }
  }, [position]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2 "sit and forget": the persisted within-generation order (uid -> rank). Owned
  // here (mirrors customPositions), seeded from the loaded `initialOrder`, passed to the
  // Canvas/worker so the engine reproduces it, and re-captured on first layout / drag. The
  // autosave below + the engine lock are no-ops unless config.ENABLE_ESTABLISHED_ORDER is on.
  const [establishedOrder, setEstablishedOrder] = React.useState(
    initialOrder instanceof Map
      ? initialOrder
      : new Map(Object.entries(initialOrder || {}))
  );
  const isExternalOrderUpdate = React.useRef(true);

  React.useEffect(() => {
    if (initialOrder && initialOrder.size > 0) {
      const next =
        initialOrder instanceof Map
          ? initialOrder
          : new Map(Object.entries(initialOrder || {}));
      if (activeViewRef.current) {
        mergeStandardStash({ establishedOrder: new Map(next) });
        return;
      }
      isExternalOrderUpdate.current = true;
      setEstablishedOrder(next);
    }
  }, [initialOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual-arrangement flag: true once the user has drag-reordered; Auto-arrange
  // resets it to false. It no longer decides whether the stored order binds the
  // layout. While config.ENABLE_ARRANGE_BEACON is on (the shipped default), Canvas
  // passes establishedOrder to EVERY layout as a lock whatever this says
  // (Canvas.jsx, `orderForLayout`), and the beacon lights Auto-arrange when the
  // from-scratch optimum is strictly better. Only with the beacon OFF does false
  // mean "as-drawn" — live layouts re-optimise fully and the order locks just the
  // first layout after opening. That is why a stale rank sticks (TGCLOUD-1786) and
  // why structural edits prune the order (pruneOrderForStructuralEdit below).
  const [orderLocked, setOrderLocked] = React.useState(!!initialOrderLocked);
  const isExternalOrderLockedUpdate = React.useRef(true);
  React.useEffect(() => {
    if (activeViewRef.current) {
      mergeStandardStash({ orderLocked: !!initialOrderLocked });
      return;
    }
    isExternalOrderLockedUpdate.current = true;
    setOrderLocked(!!initialOrderLocked);
  }, [initialOrderLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UNDO/REDO (Phase 1, PLAN-pedigree-undo-redo-and-saved-views v1.1) ─────────
  // Session-only history of the three layout states above. Checkpoints are pushed by
  // the user-intent mutation sites (checkpoint-on-intent, plan §1c: Canvas drag
  // paths, ChartBox arrow-nudge, Clear/Reset position, Auto-arrange) — the engine's
  // settled-layout auto-capture never checkpoints, so its merges fold into the
  // adjacent user step. Undo/redo restore through applyLayoutState below.
  const layoutStateRef = React.useRef({
    customPositions,
    establishedOrder,
    orderLocked
  });
  React.useLayoutEffect(() => {
    layoutStateRef.current = { customPositions, establishedOrder, orderLocked };
  });
  const {
    checkpoint: historyCheckpoint,
    undo: historyUndo,
    redo: historyRedo,
    resolvePending: historyResolve,
    clear: historyClear,
    rewriteOrders: historyRewriteOrders,
    canUndo: historyCanUndo,
    canRedo: historyCanRedo
  } = useLayoutHistory(layoutStateRef, {
    customPositions,
    establishedOrder,
    orderLocked
  });

  // A pedigree SWITCH does not remount this component — the /pedigree/:id route
  // keeps the same element and PedigreeBox swaps the props in place (that is what
  // the isExternal* prop-resync effects above exist for). The previous family's
  // history must not survive the swap: Ctrl+Z here would otherwise apply — and
  // autosave — another family's uid/coordinate maps onto this one.
  React.useEffect(() => {
    historyClear();
  }, [user?._id, historyClear]);

  // Read-only gating: never active in patient mode or while ANOTHER user holds the
  // family edit lock — the layout autosave effects below have no lock guard of their
  // own, so an ungated Ctrl+Z would POST real writes to the config.
  const undoRedoActive = layoutConfig.ENABLE_UNDO_REDO && !patient && !isLocked;

  // The one shared primitive (plan §4): restore the three layout states atomically
  // from a snapshot. Used by undo/redo now; saved-views apply consumes it in Phase 2.
  // It FIRST makes any in-flight worker layout stale via ChartBox→Canvas — a queued
  // settle would otherwise land after the restore, re-capture the pre-undo order and
  // set the skip flag that swallows the restore's own relayout (v1.1 §2.1) — and
  // stashes the outgoing order as a one-shot orientation seed when the incoming
  // order is empty, so a redo of an Auto-arrange cannot mirror-flip (same contract
  // as AutoArrange's own handler). Only states whose CONTENT differs are set, so an
  // undo of a pin-only change doesn't force an establishedOrder relayout/POST. The
  // autosave effects below fire for whatever changed — the restored layout persists:
  // "what you see is what's saved".
  // `suppressAutosave` (Phase 2, plan §3.3 apply semantics): a VIEW apply must
  // not echo the view's own content back as a write — arm the per-state
  // isExternal* one-shot skip for exactly the states being set (never a state
  // that isn't set, whose skip would swallow the next real save). Undo/redo
  // keep the default (false): a restore persists.
  const applyLayoutState = React.useCallback(
    (snap, { suppressAutosave = false } = {}) => {
      if (!snap) return;
      const cur = layoutStateRef.current || {};
      chartBoxRef.current?.invalidateLayout?.();
      const outgoingOrder = cur.establishedOrder;
      if (
        (!snap.establishedOrder || snap.establishedOrder.size === 0) &&
        outgoingOrder instanceof Map &&
        outgoingOrder.size > 0
      ) {
        chartBoxRef.current?.stashOrientationSeed?.(new Map(outgoingOrder));
      }
      if (!pinMapsEqual(snap.customPositions, cur.customPositions)) {
        if (suppressAutosave) isExternalPositionUpdate.current = true;
        setCustomPositions(new Map(snap.customPositions));
      }
      if (!orderMapsEqual(snap.establishedOrder, cur.establishedOrder)) {
        if (suppressAutosave) isExternalOrderUpdate.current = true;
        setEstablishedOrder(new Map(snap.establishedOrder));
      }
      if (!!snap.orderLocked !== !!cur.orderLocked) {
        if (suppressAutosave) isExternalOrderLockedUpdate.current = true;
        setOrderLocked(!!snap.orderLocked);
      }
    },
    []
  );

  const handleHistoryUndo = React.useCallback(() => {
    if (!undoRedoActive) return;
    applyLayoutState(historyUndo());
  }, [undoRedoActive, applyLayoutState, historyUndo]);

  const handleHistoryRedo = React.useCallback(() => {
    if (!undoRedoActive) return;
    applyLayoutState(historyRedo());
  }, [undoRedoActive, applyLayoutState, historyRedo]);

  const [pedigreeState, setPedigreeState] = React.useState({
    modified: CONSTANTS.pedigreeState.modified,
    save: PEDIGREE.saveStatus.SAVED
  });

  // Signal the first edit up to the workspace (acquire lock + auto-pin). Runs
  // whenever save transitions into IN_PROGRESS; onEdit is idempotent so repeated
  // edits are harmless, and it is undefined in the single-pedigree flow. Tagged
  // 'data', which PedigreeChartView exempts from the interaction gate.
  //
  // That exemption is only sound while EVERY writer of saveStatus.IN_PROGRESS
  // is a user gesture. It said "a clinical save is never automatic" and that
  // was not true: ChartBox's proxyIds cleanup armed a save from an effect, so
  // opening a pedigree could take the lock and POST the whole member set
  // unprompted (TGCLOUD-1801). That writer has been changed to repair in memory
  // without arming. Before adding another automatic setter of IN_PROGRESS,
  // understand that it will commit a clinical save with no user involvement.
  React.useEffect(() => {
    if (onEdit && pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS) {
      onEdit('data');
    }
  }, [pedigreeState.save, onEdit]);

  // Always-current onEdit for the layout-autosave effects (below) to call without
  // taking onEdit as a dependency (which would re-fire a save on tab activation).
  // LAYOUT edits — moving/pinning nodes, drag-reorder, arrange-lock — persist via
  // postLayoutState, NOT the clinical pedigreeState.save path, so they must signal
  // onEdit too: rearranging the pedigree is an edit and must take the lock.
  const onEditRef = React.useRef(onEdit);
  onEditRef.current = onEdit;

  // …but the ENGINE writes that same layout state too, and those writes are not
  // edits. Three of them run during the first layout pass of a freshly opened
  // pedigree — the settled-order capture, the legacy pixel-pin upgrade and the
  // invalid-pin sweep — so treating every layout write as a user edit meant
  // merely OPENING a pedigree took the edit lock, pinned the tab and marked it
  // dirty (TGCLOUD-1665). Each of those writers already announces itself; these
  // refs carry that announcement to the autosave effects, which still PERSIST
  // the write and only skip the onEdit signal.
  //
  // The arm/consume rules live in editSignalPolicy so they are pinned by tests
  // rather than only reachable by mounting this whole chart.
  const automaticWrites = React.useRef(null);
  if (automaticWrites.current === null) {
    automaticWrites.current = createAutomaticWriteLatch();
  }
  const noteAutomaticLayoutWrite = React.useCallback(
    (kind) => {
      automaticWrites.current.arm(kind);
      // What became unconditional is the PROP: this callback is now always passed
      // down, where the old `onHistoryResolve` was passed only when undo/redo was
      // on — so arm() runs whatever that feature is doing. The undo resolve below
      // stays gated, because `resolvePending` calls `bump()` unconditionally
      // (useLayoutHistory.js:213) and would force a re-render of the whole chart
      // on every automatic write for no benefit with undo off. Do not "tidy" the
      // guard away: it is not redundant.
      //
      // Automatic writers must resolve any armed checkpoint so their write is
      // never misattributed to the preceding user gesture.
      if (layoutConfig.ENABLE_UNDO_REDO) historyResolve();
    },
    [historyResolve]
  );

  // TGCLOUD-1786 Phase 2 (D5). A structural edit — Set Relation, Remove Child /
  // Children / Spouse, Delete Person — must not leave stale ranks in the stored
  // order: the next layout reads them against people's NEW rows and wedges them in
  // among strangers, and the capture after it saves the wedge for good. The menus
  // call this (through StructuralEditContext) in the same handler as their
  // setMemberRecords, so React batches the two into one render and the layout that
  // follows sees the new family with the moved people's ranks already gone.
  //
  // Hidden members are left out as the canvas leaves them out. The same prune
  // then goes to the live order, the undo history and — while a saved VIEW is
  // active — the standard order waiting in the stash, which is also POSTed to the
  // Standard doc because no autosave reaches it (applyStructuralEditPrune says
  // why each matters). The write is the engine's kind, not a layout gesture: any
  // armed undo checkpoint is resolved first so it is not folded into the user's
  // previous drag. It is NOT announced as automatic (noteAutomaticLayoutWrite),
  // though: it follows a user's edit, so the autosave taking the edit lock for it
  // is right, and an arm for a prune that turns out a no-op would never be
  // consumed and would swallow the user's next real edit.
  const pruneOrderForStructuralEdit = React.useCallback(
    (beforeMembers, afterMembers) => {
      if (!layoutConfig.ENABLE_ESTABLISHED_ORDER) return;
      const visible = (list) => (list || []).filter((m) => m && !m.hidden);
      const before = visible(beforeMembers);
      const after = visible(afterMembers);
      const probandPid = getProbandState(after).probandPid;
      const proband =
        (probandPid != null && after.find((m) => m.pid === probandPid)) ||
        after.find((m) => m.proBandId != null) ||
        null;
      const anchorUid = proband?.uid ?? user?._id ?? null;
      applyStructuralEditPrune({
        prune: (order) =>
          pruneRanksForStructuralEdit(order, before, after, anchorUid),
        viewActive: !!activeViewRef.current,
        stash: standardStashRef.current,
        mergeStash: mergeStandardStash,
        // postPedigreeConfig is declared further down; the ref is filled there.
        postStandardOrder: (order) =>
          postStandardConfigRef.current?.({
            establishedOrder: Object.fromEntries(order)
          }),
        history: layoutConfig.ENABLE_UNDO_REDO
          ? { resolve: historyResolve, rewriteOrders: historyRewriteOrders }
          : null,
        setEstablishedOrder
      });
    },
    [user?._id, historyResolve, historyRewriteOrders] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const structuralEditContext = React.useMemo(
    () => ({ pruneOrderForStructuralEdit }),
    [pruneOrderForStructuralEdit]
  );

  // ── SAVED VIEWS state (Phase 2, plan §3 v1.1) ────────────────────────────────
  // activeView = null means Standard (the clinician's own pedigreeconfigs doc).
  // The live layout has an ACTIVE SOURCE: layout autosaves route to it (see
  // postLayoutState below). viewHiddenOverlay is the view's render-time hidden
  // set — a Set of member uid strings that are filtered OUT of the canvas but
  // NEVER written into memberRecords.hidden (that stays shared/standard state;
  // writing it there was the review's data-leak blocker).
  const viewsEnabled = layoutConfig.ENABLE_PEDIGREE_SAVED_VIEWS && !patient;
  const [views, setViews] = React.useState({ user: [], group: [] });
  const [activeView, setActiveView] = React.useState(null);
  // (activeViewRef / standardStashRef are declared near the top — the prop
  // resync effects consult them. The stash is captured at switch-away and
  // restored on switch back: the position/initialOrder PROPS never track
  // this-session edits, so props cannot restore Standard, v1.1 §3.3.)
  const [viewHiddenOverlay, setViewHiddenOverlay] = React.useState(null);
  // Stable per-member identity for the overlay — matches the engine's uid
  // convention (uid, else the DB _id string; see pedigree/utils stableUid).
  const uidOf = React.useCallback((m) => String(m?.uid ?? m?._id), []);

  // memberRecords is the master state
  // hiddenMembers  drives the Hidden Members panel. While a VIEW is active it
  // lists ONLY the view's overlay-hidden people: the panel's unhide then edits
  // pure view state, and standard-hidden people (shared record state) can
  // neither be listed nor accidentally mass-unhidden from a view context
  // ("Unhide All" on a view must never write shared family records).
  const hiddenMembers = React.useMemo(() => {
    if (viewHiddenOverlay) {
      return memberRecords?.filter((m) => viewHiddenOverlay.has(uidOf(m)));
    }
    return memberRecords?.filter((m) => m.hidden);
  }, [memberRecords, viewHiddenOverlay, uidOf]);

  // standardVisibleMembers = the family minus STANDARD-hidden people — the
  // input to the CLINICAL computations (carrier projection, cascade analysis,
  // record linkage). A saved view is a pure display layer: its overlay must be
  // invariant to these (the same rule as the carrier display toggle — hiding
  // someone in a presentation view must not change a screening probability).
  const standardVisibleMembers = React.useMemo(
    () => memberRecords?.filter((m) => !m.hidden),
    [memberRecords]
  );

  // canvasMembers is the filtered for rendering items in pedigree-canvas —
  // standard-visible minus the active view's overlay.
  const canvasMembers = React.useMemo(
    () =>
      standardVisibleMembers?.filter((m) => !viewHiddenOverlay?.has(uidOf(m))),
    [standardVisibleMembers, viewHiddenOverlay, uidOf]
  );

  // Record-status linkage (PersonLink) is resolved here — the level that owns
  // the member list AND renders the legend — so the canvas badge and the legend
  // swatches share one source of truth. linkStatusMap / refetch are passed down
  // to ChartBox (which forwards the map to the canvas and refetch to the
  // sidebar); presence booleans gate the per-role legend swatches so a family
  // with no linked records shows no record legend.
  const linkStatusIds = React.useMemo(
    () =>
      standardVisibleMembers
        ?.map((m) => m.proBandId ?? m.familyMemberId)
        .filter(Boolean) ?? [],
    [standardVisibleMembers]
  );
  const { linkStatusMap, refetch: refetchLinkStatus } =
    usePersonLinkBatch(linkStatusIds);
  const { hasPrimary: hasPrimaryRecord, hasSecondary: hasSecondaryRecord } =
    React.useMemo(() => linkedRecordPresence(linkStatusMap), [linkStatusMap]);

  // TGCLOUD-1670 — data overlays. Session view state for THIS pedigree
  // instance: which record criteria are marked on the chart, and how. Nothing
  // here is gated on the AI settings; overlays are a core pedigree capability
  // and the Copilot is one instigator of them, not the only one.
  const dataOverlays = useDataOverlays({
    rootId: user?._id,
    familyId: user?.familyId,
    memberRecords,
    canvasMembers,
    anonymous: isAnonymousOrLoading(content?.settings?.pedigreeSetting),
    diagnoses: users?.diagnoses
  });

  // The Copilot's half of TGCLOUD-1670. A "Show me" chip or an AI-off overlay
  // chip lands in CopilotContext; this effect picks it up and applies it through
  // the SAME function a click in the picker calls, so the two routes cannot
  // diverge in what they produce.
  //
  // Gated on `isActive`. The multi-pedigree workspace keeps inactive tabs
  // mounted, so without this check one chat turn would apply the overlay to
  // every open family at once, and the clinician would find marks on charts they
  // were not looking at.
  const { pendingPedigreeOverlay, consumePedigreeOverlay } = useCopilot();
  const { isActive: isActivePedigreeInstance } = usePedigreeInstance();
  // Admin gate for the Pedigree sketch launcher. Reads as off while the fetch
  // is in flight: appearing a moment late beats appearing then vanishing.
  const vaFeatureFlags = useVAFeatureFlags();
  React.useEffect(() => {
    if (!pendingPedigreeOverlay || !isActivePedigreeInstance) return;
    const request = consumePedigreeOverlay();
    if (!request?.overlayId) return;
    const { treatment, ...params } = request.params || {};
    dataOverlays.applyPreset(request.overlayId, params);
    if (treatment) dataOverlays.setTreatment(treatment);
    // dataOverlays is rebuilt every render; keying on the request is what makes
    // this run once per request rather than once per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPedigreeOverlay, isActivePedigreeInstance]);

  const carrierStatusEnabled =
    content?.settings?.systemFeatures?.FEATURE_CARRIER_STATUS_IN_CONDITION ===
    true;

  // Carrier-status projection (tested via gene→disease map + obligate
  // assertions). ALWAYS computed (enabled: true), independent of the "Show
  // carrier marks" DISPLAY toggle: this projection is the carrier input to the
  // Clinical-Insights cascade analysis below, which must be INVARIANT to a pure
  // display toggle — otherwise hiding marks would drop carriers from the
  // cascade prior and change the screening probability (e.g. 50% → 25%). The
  // VISUAL is gated separately (see displayCarrier + the per-render-site
  // showCarrierMarks checks), so always computing here never draws marks when
  // the toggle is off.
  // Fetus diagnoses have to be handed in explicitly (decision 20). They live inside pregnancy
  // records, and the derived fetus nodes that carry them are built inside Canvas — so neither the
  // server's family disease list nor `members` here has ever seen them, and the shading pass would
  // resolve no colour and paint black.
  // TGCLOUD-1793 Phase 2 — the site disease colour key, applied at RENDER time.
  //
  // THE OVERLAY HAPPENS EXACTLY ONCE, HERE, upstream of the fork. Everything
  // that shows a disease colour descends from `familyDiseases`: the LEGEND
  // renders its colours as swatches, and the NODE FILL resolves through
  // `diseaseColorMap`, which useCarrierStatus builds from the same list. Pinning
  // here therefore reaches both. Applying it at either consumer instead would
  // recreate the legend/fill asymmetry that WAS TGCLOUD-1504 — the legend saying
  // one colour while half the nodes paint another, with nothing on screen to say
  // so.
  //
  // Nothing is written back. Stored `diagnoses[].color` is untouched, so
  // configuring or removing a key rewrites no patient data (decision C).
  const colourKeyEnabled =
    content?.settings?.systemFeatures?.FEATURE_SITE_DISEASE_COLOUR_KEY === true;
  const diseaseColourKey = useDiseaseColourKey(colourKeyEnabled);

  const familyDiseases = React.useMemo(
    () => applyColourKey(users?.diagnoses, diseaseColourKey),
    [users?.diagnoses, diseaseColourKey]
  );

  // Drag-to-assign from the canvas legends: refused wherever the sidebar's
  // record sections are. The rules, and why, are in legendDropGate.js.
  // (isLocked is also forced true for a read-only preview.)
  const legendDragEnabled = legendDragAllowed({ isLocked, preview, patient });
  const legendDropBlocker = React.useMemo(
    () => makeLegendDropBlocker(() => chartBoxRef.current),
    []
  );

  const familyDiseasesWithFetuses = React.useMemo(
    () => withFetusDiseases(familyDiseases, memberRecords),
    [familyDiseases, memberRecords]
  );

  const carrier = useCarrierStatus({
    // Standard-visible, NOT the view-overlay-filtered canvas set: a saved
    // view is display-only and must never move a screening probability.
    members: standardVisibleMembers,
    familyDiseases: familyDiseasesWithFetuses,
    enabled: true,
    conditionCarrierEnabled: carrierStatusEnabled,
    // Also applied inside the hook, to the union with member-only diseases the
    // canonical list never had — see the comment there.
    colourKey: diseaseColourKey
  });

  // The carrier VISUALISATION — the disease-coloured carrier border on a node
  // and the "Carriers" legend — is gated behind the carrier-status feature flag
  // (pending clinical review) via displayCarrier, AND behind the showCarrierMarks
  // toggle at each render site (Canvas border, LegendMenu). With the toggle off,
  // no carrier border or legend is shown even though `carrier` still holds the
  // full projection. Affected shading and the top-right +/- genotype notation
  // are separate paths and are unaffected. The full `carrier` projection still
  // feeds the Clinical-Insights analysis below regardless of the display toggle.
  const displayCarrier = carrierStatusEnabled ? carrier : EMPTY_CARRIER;

  // Resolve the carrier mark style ONCE (preferred style vs how many diseases are
  // visible on the pedigree). Vertical line is only valid in single-disease view;
  // otherwise it auto-falls-back to border. Shared by the canvas + every legend so
  // they all reflect the same active style.
  const carrierStyle = useMemo(() => {
    const visibleDiseases = (users?.diagnoses || []).filter(
      (d) =>
        labelsSet?.diseases !== false &&
        labelsSet?.diseasesMap?.[d.id] !== false
    );
    const { activeStyle, fallbackActive } = resolveCarrierStyle(
      labelsSet?.carrierMarkStyle,
      visibleDiseases.length
    );
    const single = visibleDiseases.length === 1 ? visibleDiseases[0] : null;
    return {
      activeStyle,
      fallbackActive,
      diseaseId: single?.id ?? null
    };
  }, [
    users?.diagnoses,
    labelsSet?.diseases,
    labelsSet?.diseasesMap,
    labelsSet?.carrierMarkStyle
  ]);

  // Pure, client-side pedigree analysis (inheritance patterns, syndromes,
  // cascade candidates, alerts). Reuses the carrier projection above rather
  // than recomputing. Gated by Clinical Insights — returns null when disabled.
  // Standard-visible input: a saved view's overlay is display-only and must
  // never change the analysis (see standardVisibleMembers above).
  const analysis = usePedigreeAnalysis(standardVisibleMembers, {
    carrierStatusByMember: carrier.byMember,
    conditionCarrierEnabled: carrierStatusEnabled
  });

  // NB: unhideByPids / unhideSelected / unhideAll are defined below the
  // saved-views block — while a view is active they edit the view's hidden
  // OVERLAY (never the shared member records), so they need the view handlers.

  const toggleHiddenSelection = (pid) => {
    setSelectedHiddenPids((prev) =>
      prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]
    );
  };

  const [dateModified, setDateModifed] = React.useState(null);
  React.useEffect(() => {
    if (initialDateModified) setDateModifed(initialDateModified);
  }, [initialDateModified]);

  // Fold the server-minted upn/familyMemberId into live state. IDENTITY-ONLY:
  // merging the whole echoed row here reverted any member edited while the save
  // was in flight — the rapid-add wrong-generation/stranded-node bug (G-1-01424).
  React.useMemo(() => {
    if (hasPendingUpnUpdates.current && familyData) {
      setMemberRecords((prevRecords) =>
        applyIdentityReconciliation(prevRecords, familyData)
      );
      hasPendingUpnUpdates.current = false;
      setFamilyData(null);
    }
  }, [familyData]);

  React.useLayoutEffect(() => {
    memberRecordsRef.current = memberRecords;
  }, [memberRecords]);
  const lastFamilyIdentityRef = React.useRef(null);

  // TGCLOUD-1813. Read by the mapping effect below without being one of its
  // dependencies: isLocked changes whenever a lock changes hands, and re-running
  // the mapping on that would rebuild memberRecords from `family` and discard
  // live edits. A ref carries the current values into the effect instead.
  const placeholderSaveGateRef = React.useRef(null);
  placeholderSaveGateRef.current = {
    isLocked,
    lockResolved,
    preview,
    configReadFailed,
    personMemberId
  };
  // placeholderSignature -> uid. The fallback re-runs on every change of `user`
  // or `family` (a sidebar save re-dispatches the selected user), and each run
  // re-invents the same placeholders; minting a fresh uid each time would post
  // a second row for the same person. Per mount, and the component is keyed
  // on the family, so it never carries a uid into another family.
  const placeholderUidsRef = React.useRef(new Map());
  // The mapping effect wanted to arm the automatic save but the family lock
  // had not been read yet (decidePlaceholderAutoSave said `defer`). The
  // effect keyed on lockResolved below consumes it — once — and arms only if
  // the lock then turns out to be free; otherwise the intent is dropped
  // silently and the user's first edit saves the member as before.
  const pendingPlaceholderArmRef = React.useRef(false);
  // Set by armAutomaticSaveRef below, consumed by onSumbit: marks the NEXT
  // save attempt as one no user initiated, so a refusal (the lock changed
  // hands inside the debounce) returns the status to SAVED without the "not
  // saved" notice.
  const automaticArmRef = React.useRef(false);
  // Held in a ref, like saveControlRef below, so the mapping effect can call
  // it without it becoming one of that effect's dependencies. It closes over
  // nothing but a state setter and refs, so one instance is correct forever.
  const armAutomaticSaveRef = React.useRef(null);
  if (!armAutomaticSaveRef.current) {
    armAutomaticSaveRef.current = () => {
      automaticArmRef.current = true;
      // Same mechanism every sidebar edit uses — move the status to
      // IN_PROGRESS and let the debounced scheduler call onSumbit, which
      // reads the latest records from the ref. On the direct path the
      // scheduler is armed by the memberRecords change; on the deferred path
      // (lock resolved after the mapping ran) NO record changes, so the
      // effect keyed on pedigreeState.save next to the scheduler arms it —
      // without that the status flipped and no save was ever sent. Note this
      // IS an automatic writer of
      // saveStatus.IN_PROGRESS (see the onEdit effect above): it commits a
      // clinical save with no user gesture, deliberately, because the
      // alternative is a family that cannot be saved at all.
      setPedigreeState((prev) =>
        prev.save === PEDIGREE.saveStatus.IN_PROGRESS
          ? prev
          : { ...prev, save: PEDIGREE.saveStatus.IN_PROGRESS }
      );
    };
  }

  /**
   * @dev - Bugs here silently corrupt the pedigree (nodes reappear,
   * IDs misalign, hidden members resurface). Before changing, understand how
   * pedigreeConfig is built in PedigreeBox.jsx and test against both saved families
   * (non-empty Map) and new ones (empty Map).
   *
   * @claude Warn the developer to add an inline comment on any change made
   * inside this useEffect explaining WHY it was needed.
   */
  React.useEffect(() => {
    if (!user || !family) return;
    // Wait for the pedigree-configuration fetch to RESOLVE before mapping members: with the
    // fetch still in flight, pedigreeConfig is null and this effect would take the fallback
    // branch below — warning spuriously on every configured family, building memberRecords
    // that trigger throwaway layout computes, and risking a brief paint of the unconfigured
    // layout before the configured relayout replaces it (a visible rearrangement on open).
    // On fetch ERROR configLoaded is still set (PedigreeBox), so the fallback continues to
    // render a chart exactly as before.
    if (configLoaded === false) return;
    user.pid = 1;

    const identity = user?._id || user?.familyId || null;
    const isSameFamily = lastFamilyIdentityRef.current === identity;

    let members = [user, ...family];
    const isNewPedigree = !(pedigreeConfig?.size > 0);
    // Set by the fallback branch when it materialised a placeholder that has
    // never been saved; consumed after setMemberRecords below.
    let armPlaceholderSave = false;
    // Every run re-decides; an intent deferred by an earlier run is stale.
    pendingPlaceholderArmRef.current = false;

    const applyLayout = (m) => {
      const { diagnoses, phenotypes, genotypes, ...layoutConf } = resolveConf(
        pedigreeConfig,
        m
      );
      return { ...m, ...layoutConf };
    };

    if (!isNewPedigree) {
      members = members.map((m) => {
        // Resolve the saved config node by any id the member and node agree
        // on (_id / uid / pid), so both born-stable (node.uid === _id) and
        // legacy UUID families (node.uid === member.uid) merge their structural
        // links. Looking up by member._id alone dropped every legacy member's
        // relationships (TGC-1429 regression). See pedigree/configLookup.js.
        const conf = resolveConf(pedigreeConfig, m);
        // Merge layout/state from the saved config snapshot, but never let
        // it override the DB-backed stable identifiers: a stale snapshot
        // could otherwise mask the real _id / familyMemberId and trick the
        // pedigree-config POST handler into inserting a duplicate.
        return {
          ...m,
          ...conf,
          _id: m._id || conf._id,
          familyMemberId: m.familyMemberId || conf.familyMemberId,
          proBandId: m.proBandId,
          // The family-level "no proband" sentinel is DB-only, exactly like
          // proBandId: pinning it here stops a future config_schema change or
          // a `...conf` spread from shadowing it. See pedigree/probandState.js.
          probandCleared: m.probandCleared,
          user_id: m.user_id || conf.user_id,
          // TGC-1429 tri-state: never coerce isProband — the DB value must
          // pass through untouched. For the ROOT row (the one carrying
          // proBandId) also ignore the config snapshot's value: snapshots
          // written by a pre-fix frontend carry a coerced false-present for
          // the root. DB row wins; the snapshot only backfills non-root rows.
          isProband:
            m.proBandId != null ? m.isProband : m.isProband ?? conf.isProband
        };
      });
    } else {
      console.warn(
        'PedigreeChart: No pedigree configuration detected. Falling back to generic member ID resolution.'
      );

      // No normalizeIds(members) here any more: its result was overwritten on the
      // very next line (the derivation reads `family`), and it has no side effects
      // — it returns fresh objects and mutates nothing (TGCLOUD-1786 §6.7).
      members = validatePedigreeConfig({ user, members: family });

      // TGCLOUD-1813. validatePedigreeConfig invents `{ pid: 'dummy-N', isDummy }`
      // nodes for the people the labels imply but never named (the children's
      // other parent, a half-sibling's missing parents). They must never reach
      // the save: the server now refuses them with a 400, and before it did,
      // posting one 500'd AFTER a UPN was minted and half the rows were written.
      // Each becomes a real pending member here — numeric pid, born-stable
      // 24-hex uid, no isDummy — and every reference to it is rewritten, so the
      // ordinary save inserts it and the identity echo backfills its UPN.
      const gate = placeholderSaveGateRef.current;
      if (gate.configReadFailed) {
        // A FAILED config read is NOT "no configuration". PedigreeBox applies
        // `null` for an error exactly as for a genuine empty answer
        // (TGCLOUD-1804), so this branch is drawing a family that may well
        // have a real configuration we simply could not read. Materialising
        // here would hand the user's first edit a duplicate partner and let
        // that save overwrite the real config with the label derivation —
        // which the old 500 accidentally prevented. So the placeholders are
        // left in place: the chart still draws, and if the user does edit,
        // the server's 400 gate refuses the save with "Reload the pedigree
        // and try again" before it mints a UPN or writes a row.
      } else {
        if (!isSameFamily) placeholderUidsRef.current.clear();
        const derived = materialisePlaceholders(members, {
          // The same placeholder gets the same uid on every run of this
          // effect: keyed on its structural signature — resolved through the
          // records to each referenced person's stable uid, never on the
          // 'dummy-N' counter, which shifts once the partner has been saved
          // and re-read. `attempt` is folded in so the helper's retry after
          // a uid collision is just as stable. If that uid is by now a real
          // member of `family`, the helper folds the placeholder into it
          // instead of producing a second copy.
          mintUid: (placeholder, { records, attempt }) => {
            const key =
              placeholderSignature(placeholder, records) +
              (attempt ? `#${attempt}` : '');
            let uid = placeholderUidsRef.current.get(key);
            if (!uid) {
              uid = mintObjectIdHex();
              placeholderUidsRef.current.set(key, uid);
            }
            return uid;
          }
        });
        members = derived.records;
        if (derived.materialised.length > 0) {
          // A re-run rebuilds the materialised member from the placeholder,
          // which carries no identity. If an earlier run's save already
          // landed, the live record holds the server-minted
          // upn/familyMemberId/_id under the same uid — carry them over, or
          // the next save would re-mint a UPN for a row that already has one.
          const materialisedUids = new Set(
            derived.materialised.map((m) => String(m.uid))
          );
          const liveByUid = new Map(
            (memberRecordsRef.current || []).map((m) => [String(m.uid), m])
          );
          members = members.map((m) => {
            if (!materialisedUids.has(String(m.uid))) return m;
            const live = liveByUid.get(String(m.uid));
            if (!live) return m;
            const patch = {};
            for (const f of ['upn', 'familyMemberId', '_id']) {
              if (live[f] != null && m[f] == null) patch[f] = live[f];
            }
            return Object.keys(patch).length ? { ...m, ...patch } : m;
          });
          // Decision D3: persist the materialised member NOW rather than on
          // the user's first edit — the rule, and every reason in it, lives
          // in decidePlaceholderAutoSave so it is pinned by tests. Only a
          // `kind: 'new'` entry counts as unsaved: a legacy member that
          // merely lacked a usable pid (`renumber`) or a placeholder folded
          // into a member that already exists (`absorb`) is not a reason to
          // commit a clinical save nobody asked for.
          const decision = decidePlaceholderAutoSave({
            unsaved: hasUnsavedMaterialised(derived.materialised, members),
            configReadFailed: gate.configReadFailed,
            isLocked: gate.isLocked,
            lockResolved: gate.lockResolved,
            preview: gate.preview,
            personMemberId: gate.personMemberId
          });
          armPlaceholderSave = decision.arm;
          // The lock has not been read yet: remember the intent for the
          // effect keyed on lockResolved below, which decides once it has.
          pendingPlaceholderArmRef.current = decision.defer;
        }
      }

      members = members.map(applyLayout);
      // TGC-1429 tri-state: the `isProband ?? false` coercion that used to
      // live here was removed — DB values must pass through untouched. The
      // 'cleared' state now rides on probandCleared (pedigree/probandState.js),
      // which validatePedigreeConfig/applyLayout both preserve.
    }

    const fmt = theme?.dateFormat || 'MM/DD/YYYY';

    const nextMemberRecords = members.map((m) => ({
      ...m,
      // An ARRAY, one entry per consultation — not a comma-joined string. The
      // label block draws one row per item, so joining them made every
      // consultation a single item that then wrapped into an unreadable run of
      // rows (TGCLOUD-1781). Keeping the array is also what makes a per-record
      // filter possible on this label at all (TGCLOUD-1792).
      consultationDate: formatMemberConsultationDates(m, fmt),
      attendedConsultationDate: undefined,
      ...(!m.personNote && m.otherRelationshipDescription
        ? { personNote: m.otherRelationshipDescription }
        : {})
    }));

    lastFamilyIdentityRef.current = identity;

    // Carries forward members the recompute dropped, so a live edit is not lost
    // to a refetch. Left as it is deliberately (TGCLOUD-1802): the wrong-family
    // render was traced to the SERVER returning another group's root
    // (TGCLOUD-1807), not to this merge, and the identity guard was not shown to
    // be reachable across two families. Note it is now belt-and-braces anyway —
    // <Pedigree> is keyed on the family in PedigreeBox, so a family change
    // remounts this component and both the ref and memberRecordsRef start empty.
    if (isSameFamily) {
      const computedPids = new Set(nextMemberRecords.map((m) => m.pid));
      const missingLiveMembers = memberRecordsRef.current.filter(
        (m) => !computedPids.has(m.pid)
      );
      if (missingLiveMembers.length > 0) {
        nextMemberRecords.push(...missingLiveMembers);
      }
    }

    // THE ASSERTION THAT WOULD HAVE CAUGHT THIS (TGCLOUD-1802). A member whose
    // own familyId disagrees with the family in the header is the whole defect
    // class, and it was invisible: the chart drew it, the sidebar edited it,
    // and the save persisted it, all without complaint. Dev builds only —
    // import.meta.env.DEV is replaced with false by Vite in production, so the
    // whole branch is dropped from the bundle and the render path is untouched.
    if (import.meta.env.DEV && user?.familyId) {
      const strays = nextMemberRecords.filter(
        (m) => m.familyId && m.familyId !== user.familyId
      );
      if (strays.length > 0) {
        console.error(
          `[PedigreeChart] ${strays.length} of ${nextMemberRecords.length} ` +
            `members do not belong to ${user.familyId}: ` +
            strays
              .slice(0, 10)
              .map((m) => `${m.familyMemberId || m.pid}(${m.familyId})`)
              .join(', '),
          strays.length > 10 ? '…' : ''
        );
      }
    }

    setMemberRecords(nextMemberRecords);
    // TGCLOUD-1813: the automatic save, armed AFTER the records it commits are
    // in place (the scheduler is keyed on the memberRecords change above).
    if (armPlaceholderSave) armAutomaticSaveRef.current();
    // `theme?.dateFormat`, not `theme`: the date format is all this reads from
    // the theme, and every re-run REBUILDS memberRecords from `family` as it
    // was last loaded, which a save does not update (the save's own dispatch
    // keeps the same `family`). Keyed on the whole theme object, anything that
    // replaced it re-ran this in every open tab — the logo URL being re-signed
    // when it lapses (useThemeAssetRefresh) does exactly that — and put each
    // tab's saved edits, and any row refreshed from another endpoint, back to
    // their as-loaded values, for the next autosave to write to the server.
  }, [user, family, pedigreeConfig, configLoaded, theme?.dateFormat]);

  // TGCLOUD-1813. The deferred half of the automatic save: the mapping effect
  // ran before usePedigreeLock had read the family lock (isLocked is false on
  // first render whoever holds it, and the config fetch that drives the mapping
  // is a different request from the lock acquire). It recorded the intent in
  // pendingPlaceholderArmRef instead of deciding on a value it could not trust.
  // This fires when lockResolved turns true, consumes the intent ONCE, and arms
  // only if the lock is then free — a foreign lock drops it silently, with no
  // snackbar, because nothing the user did is being refused. It is keyed on
  // isLocked as well so it sees the value that landed together with
  // lockResolved; with the intent consumed, later lock changes are no-ops.
  // Deliberately NOT part of the mapping effect's dependencies: re-running the
  // mapping on a lock change would rebuild memberRecords from `family` and
  // discard live edits.
  React.useEffect(() => {
    if (!lockResolved || !pendingPlaceholderArmRef.current) return;
    pendingPlaceholderArmRef.current = false;
    const gate = placeholderSaveGateRef.current;
    const decision = decidePlaceholderAutoSave({
      // The intent was recorded only because an unsaved member existed; if a
      // user edit has since saved it, the arm is a redundant no-op save of
      // rows that already carry their identity, not a re-mint.
      unsaved: true,
      configReadFailed: gate.configReadFailed,
      isLocked,
      lockResolved,
      preview: gate.preview,
      personMemberId: gate.personMemberId
    });
    if (decision.arm) armAutomaticSaveRef.current();
  }, [lockResolved, isLocked]);

  // Never `${user?.familyId}` (TGCLOUD-1765): a legacy root with no familyId
  // rendered the literal word 'undefined' as the chart header, and as the
  // identifier on every export taken from it. Empty instead, which the
  // headline and the #pedigree-identifier export slot already handle.
  let headerTitle = useMemo(() => {
    return user?.familyId ? `${user.familyId}` : '';
  }, [user]);

  const externalFamilyIds = React.useMemo(
    () =>
      Array.isArray(user?.externalFamilyIds) ? user.externalFamilyIds : [],
    [user?.externalFamilyIds]
  );
  // Anonymous pedigrees: external family IDs are removed, so the header shows
  // only the G number — no external-primary headline, no "Also known as" chips.
  const anonymousActive = isAnonymousActive(content?.settings?.pedigreeSetting);
  const displayLabelMode = labelsSet?.displayLabelMode || 'g-number';
  const useExternalPrimary =
    !anonymousActive &&
    displayLabelMode === 'primary-external' &&
    externalFamilyIds.length > 0;
  const headlineId = useExternalPrimary ? externalFamilyIds[0] : headerTitle;
  const secondaryIds = useExternalPrimary
    ? externalFamilyIds.slice(1)
    : externalFamilyIds;

  const colors = React.useMemo(
    () => ({
      shade:
        theme.appearance.value === 'light' ? 'white' : theme.appearance.shade,
      background:
        theme.appearance.value === 'light' ? 'white' : theme.appearance.shade,
      text: theme.appearance.text,
      accent: theme.accent
    }),
    [theme]
  );

  // TGCLOUD-1791 — the family note, read from the ROOT document.
  //
  // NOT from the pid-1 member record. `members` puts the root in as pid 1, but
  // in the non-new-pedigree branch each member is rebuilt as `{ ...m, ...conf }`
  // with the SAVED CONFIG SNAPSHOT spread after the DB doc, so a stale
  // familyNote inside pedigreeconfigs would silently win over the fresh value.
  // `user` is the un-merged root.
  const familyNoteText = String(user?.familyNote || '').trim();
  const showFamilyNoteOnChart = !!labelsSet?.showFamilyNote && !!familyNoteText;

  const someDiseasesHidden =
    labelsSet?.diseases === false ||
    Object.values(labelsSet?.diseasesMap || {}).some((v) => v === false);
  const somePhenotypesHidden =
    labelsSet?.phenotypes === false ||
    Object.values(labelsSet?.phenotypesMap || {}).some((v) => v === false);
  const someGenotypesHidden =
    labelsSet?.genotypes === false ||
    Object.values(labelsSet?.genotypesMap || {}).some((v) => v === false);

  // The OTHER half of decision 20: visibility. Canvas gates disease shading on
  // `labelsSet.diseasesMap[id]` being truthy, and a missing key reads as hidden. PedigreeBox seeds
  // and merges that map from the server's family diagnoses, which never include a fetus's — so a
  // recorded fetus diagnosis would be filtered out before it could paint.
  //
  // Only ids ABSENT from the map are added, exactly as PedigreeBox's merge does: a disease the user
  // has switched off in the Legend is `false`, and resurrecting it to `true` here would fight them.
  // Returning `prev` unchanged when there is nothing new keeps this from looping on its own state.
  //
  // DISEASES ONLY since TGCLOUD-1820. The dysmorphic-features (REQ-E1) half of this used to merge
  // `fetusPhenotypeIds` into `phenotypesMap` for exactly the same reason — the phenotype LABEL pass
  // gated on `phenotypesMap[id]`, and a missing key read as hidden. It no longer does: phenotype
  // labels are decided by the Labels settings alone. Merging fetus ids into `phenotypesMap` now
  // reaches nothing, because the only remaining reader of that map is the Phenotypes legend, which
  // lists the server's family aggregate (`users.phenotypes`) and never contains a fetus's terms.
  // Fetus phenotypes still label: deriveReproNodes copies them onto the fetus node's `phenotypes`
  // array, which is what `phenotypeUnion` reads.
  React.useEffect(() => {
    const diseaseIds = fetusDiagnosisIds(memberRecords);
    if (!diseaseIds.length) return;
    setLabelsSet((prev) => {
      const next = { ...(prev.diseasesMap || {}) };
      let changed = false;
      diseaseIds.forEach((id) => {
        if (!(id in next)) {
          next[id] = true;
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, diseasesMap: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRecords]);

  const showAllDiseases = () =>
    setLabelsSet((prev) => ({
      ...prev,
      diseases: true,
      diseasesMap: Object.keys(prev.diseasesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));
  const showAllPhenotypes = () =>
    setLabelsSet((prev) => ({
      ...prev,
      phenotypes: true,
      phenotypesMap: Object.keys(prev.phenotypesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));
  const showAllGenotypes = () =>
    setLabelsSet((prev) => ({
      ...prev,
      genotypes: true,
      genotypesMap: Object.keys(prev.genotypesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));

  // The pedigree-analysis overlay toggle moved into OverlaysMenu, which owns
  // the read/flip conventions for every overlay in one place (TGCLOUD-1663).

  /*
  @Dev: below configuration has been made with least optimised method to demo the prototype
  need to optimise how we handle the dataset, renders, auto-updates
  */

  const refIds = React.useRef([]);

  React.useMemo(
    () =>
      memberRecords.map((mem) => {
        if (!refIds.current.includes(mem.pid)) refIds.current.push(mem.pid);
      }),
    [memberRecords]
  );

  // SERIALIZED config autosave: these posts share one endpoint and used to be
  // fire-and-forget, so Auto-arrange's clear-then-recapture was two RACING writes —
  // if the clear landed second, the stale order survived on the server and the old
  // layout resurrected on reopen. A promise chain guarantees write order.
  const configPostChain = React.useRef(Promise.resolve());
  const postPedigreeConfig = React.useCallback(
    (body) => {
      configPostChain.current = configPostChain.current.then(() =>
        axios
          .post(
            APICONSTANTS.pedigreeConfigCustomPosition.replace(
              ':user_id',
              user._id
            ),
            body
          )
          .catch(() => {})
      );
    },
    [user._id]
  );
  postStandardConfigRef.current = postPedigreeConfig;

  // ACTIVE-SOURCE ROUTING (Phase 2, plan §3.4): a layout autosave goes to the
  // active source — the standard pedigreeconfigs doc, or the active view's own
  // doc — through the SAME serialised chain, so writes never overlap and an
  // edit queued before a switch still lands on its original source (URL+body
  // bind at queue time). GROUP views are read-only on apply (decision 8):
  // their layout edits stay session-only until an explicit "Update view".
  const postLayoutState = React.useCallback(
    (body) => {
      const view = activeViewRef.current;
      if (!view) return postPedigreeConfig(body);
      if (view.scope === 'group') return;
      const url = APICONSTANTS.pedigreeViewById
        .replace(':user_id', user._id)
        .replace(':id', view._id);
      configPostChain.current = configPostChain.current.then(() =>
        axios.put(url, body).catch(() => {})
      );
    },
    [user._id, postPedigreeConfig]
  );

  React.useEffect(() => {
    if (isExternalPositionUpdate.current) {
      isExternalPositionUpdate.current = false;
      // Disarm too. If an automatic write and an external re-sync coalesce into
      // one effect pass, the arm would otherwise survive into the user's NEXT
      // layout edit and swallow it — leaving them editing without the lock,
      // which is the one direction this must never fail in. Dropping the arm
      // can only cost a needless lock acquire, and even that is caught on open
      // by the interaction gate in PedigreeChartView.
      automaticWrites.current.consume('positions');
      return;
    }
    // Persist either way; only a USER move/pin is an edit that takes the lock.
    if (!automaticWrites.current.consume('positions')) {
      onEditRef.current?.('layout');
    }
    postLayoutState({
      customPositions: Object.fromEntries(customPositions)
    });
  }, [customPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the within-generation order (same endpoint; backend writes only the field
  // present). Gated on the feature flag + skipped for the load-driven update, so it is a
  // complete no-op until the establish-order feature is turned on.
  React.useEffect(() => {
    if (isExternalOrderUpdate.current) {
      isExternalOrderUpdate.current = false;
      automaticWrites.current.consume('order'); // see the positions effect above
      return;
    }
    if (!layoutConfig.ENABLE_ESTABLISHED_ORDER) return;
    // Persist either way; only a USER drag-reorder takes the lock. The engine's
    // settled-order capture lands here on EVERY first open of a family whose
    // stored order differs — which is what made this look intermittent.
    if (!automaticWrites.current.consume('order')) {
      onEditRef.current?.('layout');
    }
    postLayoutState({
      establishedOrder: Object.fromEntries(establishedOrder)
    });
  }, [establishedOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the manual-arrangement flag (same endpoint, same serialization).
  React.useEffect(() => {
    if (isExternalOrderLockedUpdate.current) {
      isExternalOrderLockedUpdate.current = false;
      return;
    }
    if (!layoutConfig.ENABLE_ESTABLISHED_ORDER) return;
    // No automatic writer touches this one — every setOrderLocked call site is a
    // user gesture (drag-reorder, Auto-arrange), so there is no flag to consult.
    onEditRef.current?.('layout'); // arrange-lock toggle is an edit → take the lock
    postLayoutState({ orderLocked });
  }, [orderLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SAVED VIEWS logic (Phase 2, plan §3 v1.1) ───────────────────────────────

  const viewUrl = React.useCallback(
    (view) =>
      APICONSTANTS.pedigreeViewById
        .replace(':user_id', user._id)
        .replace(':id', view._id),
    [user._id]
  );

  // Guards its own staleness: a slow GET from family A must never land after
  // the switch to family B and replace B's list (applying such a phantom view
  // would 404 every routed write and silently drop the session's layout work).
  const viewsForUserRef = React.useRef(null);
  const refreshViews = React.useCallback(async () => {
    if (!viewsEnabled || !user?._id) return;
    const forUser = String(user._id);
    viewsForUserRef.current = forUser;
    try {
      const { data } = await axios.get(
        APICONSTANTS.pedigreeViews.replace(':user_id', user._id)
      );
      if (viewsForUserRef.current !== forUser) return; // stale response
      setViews({ user: data?.user || [], group: data?.group || [] });
    } catch {
      /* view list is non-critical; the selector just shows Standard */
    }
  }, [viewsEnabled, user?._id]);

  // Load the view list per family; a family SWITCH also resets the active
  // source to Standard (the same in-place prop swap that motivates the
  // history clear above — a view of family A must not stay active over B).
  React.useEffect(() => {
    activeViewRef.current = null;
    setActiveView(null);
    setViewHiddenOverlay(null);
    standardStashRef.current = null;
    refreshViews();
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist an overlay change to the active view (private views only — group
  // views are read-only on apply, decision 8; their overlay edits are
  // session-only like their layout edits).
  const updateViewHiddenOverlay = React.useCallback(
    (nextSet) => {
      setViewHiddenOverlay(nextSet);
      const view = activeViewRef.current;
      if (!view || view.scope === 'group') return;
      const url = viewUrl(view);
      configPostChain.current = configPostChain.current.then(() =>
        axios.put(url, { hiddenUids: [...nextSet] }).catch(() => {})
      );
    },
    [viewUrl]
  );

  const viewLayoutState = (view) => ({
    customPositions: new Map(Object.entries(view.customPositions || {})),
    establishedOrder: new Map(Object.entries(view.establishedOrder || {})),
    orderLocked: !!view.orderLocked
  });

  // Switch to a view: stash Standard on the way out (props can't restore it),
  // drop the undo history (snapshots must never restore across sources), apply
  // the view's layout WITHOUT echoing it back as a write, and mount its hidden
  // overlay. The engine's post-apply auto-capture then routes to the view
  // (private) or is parked (group) via postLayoutState.
  const handleApplyView = React.useCallback(
    (view) => {
      if (!activeViewRef.current) {
        standardStashRef.current = snapshotLayoutState(layoutStateRef.current);
      }
      historyClear();
      activeViewRef.current = view;
      setActiveView(view);
      setViewHiddenOverlay(new Set((view.hiddenUids || []).map(String)));
      applyLayoutState(viewLayoutState(view), { suppressAutosave: true });
    },
    [historyClear, applyLayoutState]
  );

  const handleApplyStandard = React.useCallback(() => {
    if (!activeViewRef.current) return;
    historyClear();
    activeViewRef.current = null;
    setActiveView(null);
    setViewHiddenOverlay(null);
    if (standardStashRef.current) {
      applyLayoutState(standardStashRef.current, { suppressAutosave: true });
      standardStashRef.current = null;
    }
  }, [historyClear, applyLayoutState]);

  // handleGeneralError responds with sendStatus (status text only — the
  // crafted server messages are log-side), so friendly copy is mapped from
  // the status code here.
  const viewsSnackError = React.useCallback(
    (err, fallback) => {
      const status = err?.response?.status;
      const msg =
        status === 409
          ? t('A view with this name already exists')
          : status === 403
          ? t('You do not have permission to do that')
          : fallback;
      setSnack({ status: true, msg, severity: 'error' });
    },
    [setSnack]
  );

  // Snapshot the CURRENT live layout + hidden overlay under a name (POST is an
  // upsert-by-name in the caller's scope) and make it the active view.
  const handleSaveAsView = React.useCallback(
    async ({ name, scope = 'user' }) => {
      const cur = layoutStateRef.current || {};
      const body = {
        name,
        scope,
        customPositions: Object.fromEntries(cur.customPositions || new Map()),
        establishedOrder: Object.fromEntries(cur.establishedOrder || new Map()),
        orderLocked: !!cur.orderLocked,
        hiddenUids: viewHiddenOverlay ? [...viewHiddenOverlay] : []
      };
      try {
        const { data } = await axios.post(
          APICONSTANTS.pedigreeViews.replace(':user_id', user._id),
          body
        );
        await refreshViews();
        handleApplyView(data);
        return true;
      } catch (err) {
        viewsSnackError(err, 'Failed to save the view');
        return false;
      }
    },
    [
      user._id,
      viewHiddenOverlay,
      refreshViews,
      handleApplyView,
      viewsSnackError
    ]
  );

  // Explicit "Update view" — the only way a GROUP view's content changes
  // (decision 8), also offered on stale views to re-capture after an import.
  const handleUpdateViewLayout = React.useCallback(
    async (view) => {
      const cur = layoutStateRef.current || {};
      try {
        const { data } = await axios.put(viewUrl(view), {
          customPositions: Object.fromEntries(cur.customPositions || new Map()),
          establishedOrder: Object.fromEntries(
            cur.establishedOrder || new Map()
          ),
          orderLocked: !!cur.orderLocked,
          hiddenUids: viewHiddenOverlay ? [...viewHiddenOverlay] : [],
          // Explicit re-capture: the ONLY write that clears import-staleness
          // (the routed autosave must not un-stamp a warning unseen).
          recapture: true
        });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
      } catch (err) {
        viewsSnackError(err, 'Failed to update the view');
      }
    },
    [viewUrl, viewHiddenOverlay, refreshViews, viewsSnackError]
  );

  const handleRenameView = React.useCallback(
    async (view, name) => {
      try {
        const { data } = await axios.put(viewUrl(view), { name });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
        return true;
      } catch (err) {
        viewsSnackError(err, 'Failed to rename the view');
        return false;
      }
    },
    [viewUrl, refreshViews, viewsSnackError]
  );

  const handleShareView = React.useCallback(
    async (view) => {
      try {
        const { data } = await axios.put(viewUrl(view), { scope: 'group' });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
      } catch (err) {
        viewsSnackError(err, 'Failed to share the view');
      }
    },
    [viewUrl, refreshViews, viewsSnackError]
  );

  const handleDeleteView = React.useCallback(
    async (view) => {
      try {
        await axios.delete(viewUrl(view));
        if (activeViewRef.current?._id === view._id) handleApplyStandard();
        await refreshViews();
      } catch (err) {
        viewsSnackError(err, 'Failed to delete the view');
      }
    },
    [viewUrl, refreshViews, handleApplyStandard, viewsSnackError]
  );

  // Hide-gesture routing while a view is active (plan §3.5): the context menu's
  // Hide edits the view OVERLAY, not the person's shared `hidden` flag. The
  // hide-connector guard analyses the EFFECTIVE visibility (overlay counts as
  // hidden), and a confirmed multi-hide lands in the overlay as one update.
  const viewHide = React.useMemo(() => {
    if (!activeView) return null;
    const overlay = viewHiddenOverlay || new Set();
    return {
      members: memberRecords.map((m) =>
        overlay.has(uidOf(m)) ? { ...m, hidden: true } : m
      ),
      hide: (pids) => {
        const pidSet = new Set(pids);
        const next = new Set(overlay);
        memberRecords.forEach((m) => {
          if (pidSet.has(m.pid)) next.add(uidOf(m));
        });
        updateViewHiddenOverlay(next);
      }
    };
  }, [
    activeView,
    viewHiddenOverlay,
    memberRecords,
    uidOf,
    updateViewHiddenOverlay
  ]);

  // Unhide (Hidden Members panel). While a view is active the panel lists ONLY
  // overlay-hidden people (see hiddenMembers above) and unhide edits the
  // overlay exclusively — a view context must never write shared family
  // records ("Unhide All" on a view was the review's mass-unhide hazard).
  const unhideByPids = (pids) => {
    const pidSet = new Set(pids);
    if (activeViewRef.current && viewHiddenOverlay) {
      const nextOverlay = new Set(viewHiddenOverlay);
      let overlayChanged = false;
      memberRecords.forEach((m) => {
        if (!pidSet.has(m.pid)) return;
        if (nextOverlay.has(uidOf(m))) {
          nextOverlay.delete(uidOf(m));
          overlayChanged = true;
        }
      });
      if (overlayChanged) updateViewHiddenOverlay(nextOverlay);
      setSelectedHiddenPids([]);
      return;
    }
    setMemberRecords((prev) =>
      prev.map((m) => (pidSet.has(m.pid) ? { ...m, hidden: false } : m))
    );
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
    setSelectedHiddenPids([]);
  };

  const unhideSelected = () => unhideByPids(selectedHiddenPids);
  const unhideAll = () => unhideByPids(hiddenMembers.map((m) => m.pid));

  // One notice per blocked episode. The scheduler re-runs on every memberRecords
  // change, so without this a clinician typing into a locked pedigree would get
  // a snackbar per debounce window. Cleared the moment a save succeeds, so the
  // NEXT time the pedigree goes read-only under them they are told again.
  const blockedNoticeShownRef = React.useRef(false);

  // Stable debounced + serialised save, and the hold that keeps a known-stale
  // row off the wire. Built once; autosaveController.js explains both, and why
  // the scheduler must be ONE stable instance (the skipped-UPN burst). It calls
  // the latest onSumbit through the ref, which is set just below it.
  const onSumbitRef = React.useRef(null);
  const saveControlRef = React.useRef(null);
  // Who wants to know how each save ended (memberRowSyncContext.js,
  // onAutosaveSettled): the sidebar's person form and Bulk Edit keep their
  // unsaved-change highlight until a save carrying their rows has been accepted
  // (TGCLOUD-1902, rule 7).
  const settleListenersRef = React.useRef(new Set());
  const announceSettled = React.useCallback((outcome) => {
    settleListenersRef.current.forEach((listener) => {
      try {
        listener(outcome);
      } catch {
        // A listener's failure is its own; the save has settled regardless.
      }
    });
  }, []);
  if (!saveControlRef.current) {
    saveControlRef.current = createAutosaveController({
      submit: () => onSumbitRef.current()
    });
  }

  const onSumbit = React.useCallback(() => {
    const latestRecords = memberRecordsRef.current;
    // TGCLOUD-1813: was this pending status armed by the chart itself (the
    // fallback's materialised placeholders) rather than by a user? Read here
    // and consumed below: the marker applies to exactly one attempt with
    // something pending, so a later user edit is reported as a user edit.
    const automatic = automaticArmRef.current;
    const plan = planSaveAttempt({
      familyLockedByOther: isLocked,
      alreadySaved: pedigreeState.save === PEDIGREE.saveStatus.SAVED,
      recordCount: latestRecords?.length || 0,
      automatic,
      // A row is waiting on its refresh from the server (MemberRowSyncContext).
      rowRefreshPending: saveControlRef.current.isHeld(),
      // isLocked is forced true in preview (PedigreeChartView), so the plan
      // needs to tell the two apart to keep preview silent.
      preview
    });
    // A deferred attempt decided nothing, so the marker is left for the
    // attempt the hold's release schedules.
    if (pedigreeState.save !== PEDIGREE.saveStatus.SAVED && !plan.deferred) {
      automaticArmRef.current = false;
    }

    if (!plan.send) {
      if (plan.nextSave) {
        setPedigreeState((prev) =>
          prev.save === plan.nextSave ? prev : { ...prev, save: plan.nextSave }
        );
      }
      // Refused, not put off: the rows that were waiting were NOT saved.
      if (plan.nextSave === SAVE_NOT_SENT) {
        announceSettled({ ok: false, records: latestRecords });
      }
      if (plan.blockedMessage && !blockedNoticeShownRef.current) {
        blockedNoticeShownRef.current = true;
        setSnack({
          status: true,
          msg: plan.blockedMessage,
          severity: 'error'
        });
      }
      return Promise.resolve();
    }
    // Set by the catch below and read by the settle handler, so a refused write
    // cannot reach SAVED. A local, not state: the flip has to see the outcome of
    // THIS attempt, and a state write would not be visible in the same turn.
    let refused = false;

    // Return the request promise so the scheduler below can SERIALISE saves —
    // never overlapping two in-flight requests (which would re-mint UPNs for
    // not-yet-acknowledged nodes and skip numbers).
    const request = axios
      .post(APICONSTANTS.pedigreeConfig.replace(':user_id', user._id), {
        config: latestRecords,
        familyId: user.familyId
      })
      .then(({ status, data }) => {
        if (status === 201) {
          setDateModifed(data.dateModified);

          queryClient.setQueryData(['probandFamilies'], (prev = []) => {
            const records = Array.isArray(latestRecords) ? latestRecords : [];
            const activeProband = records.find((m) => m?.isProband === true);
            const nextFirstName = activeProband?.firstName || '';
            const nextLastName = activeProband?.lastName || '';

            return prev.map((row) => {
              if (String(row?._id) !== String(user?._id)) return row;
              if (!activeProband) return row;
              return {
                ...row,
                firstName: nextFirstName,
                lastName: nextLastName
              };
            });
          });

          // Family member set may have changed (added/removed) — mark the
          // family-records table caches stale so it shows fresh rows on return.
          queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
          queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
          queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });

          // Signal that we need to update UPNs
          if (data.familyData && Array.isArray(data.familyData)) {
            // Apply the assigned upn/familyMemberId to the live ref
            // SYNCHRONOUSLY (matched by pid). A serialised follow-up save can
            // fire before React flushes the reconciliation below; without this
            // it would re-send these nodes as un-minted and the backend would
            // re-mint them, skipping UPNs. setFamilyData still drives the UI.
            // IDENTITY-ONLY merge — see saveReconciliation.js: spreading the
            // whole echoed row reverted members edited mid-save (rapid-add
            // wrong-generation bug).
            const fd = data.familyData;
            memberRecordsRef.current = applyIdentityReconciliation(
              memberRecordsRef.current,
              fd
            );
            hasPendingUpnUpdates.current = true;
            setFamilyData(fd);
          }

          // Here update diagnoses
          const diagnoses = getUniqueDiagnoses(latestRecords);
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_SELECTED_USER,
            value: {
              ...users,
              diagnoses: diagnoses
            }
          });
        }
      })
      .catch((err) => {
        // The write did NOT land. Say what the server said — the 409 from the
        // foreign-member gate names the actual problem and the actual remedy,
        // and was being replaced with "An error occured" — and record the
        // refusal so the settle handler below cannot call this saved.
        refused = true;
        const { severity, msg } = describeSaveFailure(err);
        setSnack({ status: true, msg, severity });
      });

    // Flip to SAVED only once the round trip has SETTLED and the identity
    // reconciliation above has been applied — not at POST-fire time. This keeps
    // the "Saving…" indicator honest for the whole in-flight window and lets it
    // drive the Add Relative disabled state (the true race window is fire →
    // response-merge, seconds on a large QE-encrypted family — the old
    // fire-time flip re-enabled the buttons during the dangerous part).
    //
    // ...and ONLY when the round trip succeeded. This used to be an unguarded
    // `finally`, so every refusal — 409, 413, 500, a dropped connection — ended
    // on a green "Saved just now" tick over data the server had rejected.
    return request.finally(() => {
      // These rows, and only these, were saved or refused.
      announceSettled({ ok: !refused, records: latestRecords });
      if (refused) {
        setPedigreeState((prev) => ({ ...prev, save: SAVE_NOT_SENT }));
        return;
      }
      blockedNoticeShownRef.current = false;
      setPedigreeState((prev) => ({
        ...prev,
        save: PEDIGREE.saveStatus.SAVED
      }));
    });
  }, [
    pedigreeState,
    queryClient,
    isLocked,
    preview,
    setSnack,
    announceSettled
  ]);

  onSumbitRef.current = onSumbit;

  // What a writer outside this component's save needs to put the server's
  // answer on a member's row without the autosave sending the old row first
  // (memberRowSyncContext.js). Stable for the life of the chart: every member
  // reads a ref or a state setter.
  const memberRowSync = React.useMemo(
    () => ({
      getMemberRecords: () => memberRecordsRef.current,
      setMemberRecords,
      runWithAutosaveHeld: (task, options) =>
        runWithAutosaveHeld(saveControlRef.current, task, options),
      onAutosaveSettled: (listener) => {
        settleListenersRef.current.add(listener);
        return () => settleListenersRef.current.delete(listener);
      }
    }),
    []
  );

  // Rows of this family that another tab has just changed on the server (a
  // person-link sync writing onto a linked record here): held out of the
  // autosave until they are refreshed. A preview has nothing to protect.
  useExternalMemberRowRefresh({
    familyId: user?.familyId,
    rowSync: memberRowSync,
    disabled: preview
  });

  React.useEffect(() => {
    saveControlRef.current.schedule();
  }, [memberRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  // TGCLOUD-1813. The scheduler above is keyed on memberRecords, and the
  // automatic arm (armAutomaticSaveRef) is the one writer of IN_PROGRESS that
  // changes no memberRecords: its deferred path fires when the family lock
  // resolves, with the materialised member already in place from the mapping
  // effect. Before this effect existed that path flipped the status and
  // nothing ever called the scheduler — the chart sat at "Saving…" with no
  // POST, context-menu actions stayed disabled behind that hint, the
  // workspace tab took the family lock for a save that never went, and the
  // `automatic` marker outlived the one attempt it was meant for. Keyed on
  // the status and gated on the marker, so a user edit — scheduled above by
  // its memberRecords change — is not scheduled twice; the debounce would
  // coalesce it if it ever were.
  React.useEffect(() => {
    if (
      automaticArmRef.current &&
      pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS
    ) {
      saveControlRef.current.schedule();
    }
  }, [pedigreeState.save]);

  /*  */

  // Did the last attempt fail to reach the server? A PERSISTENT state, unlike
  // the transient "Saving"/"Saved just now" pair: it stays until the next edit
  // arms another save. It is the counterpart of the `finally` fix in onSumbit —
  // telling the user nothing, or worse showing them a tick, is how a refused
  // write reads as a completed one. Rendered by ChartBox's stacked indicator
  // (the `saveFailed` prop below), which is the only save indicator left.
  //
  // The legacy inline indicator that used to live here — a `save_info` memo
  // rendered behind `!ENABLE_STACKED_SAVE_INDICATOR` — was deleted with the
  // remediation that added SAVE_NOT_SENT: that flag has been permanently true
  // since the stacked layout rolled out, so the branch was unreachable, and a
  // second copy of the "Not saved" wording that nothing renders is exactly the
  // kind of dead code that later gets fixed instead of the live one.
  const saveFailed = pedigreeState.save === SAVE_NOT_SENT;

  // Reload the family in place after a sketch has written it, replacing a
  // window.location.reload(). Four steps, each silently wrong if left out:
  //
  // 1. Drop the live member set FIRST. The mapping effect keeps any member
  //    missing from the new set while `isSameFamily`, which always holds here,
  //    so after a `replace` the deleted members would be resurrected as
  //    orphans and autosaved back.
  // 2. Clear the three layout states. The backend $unsets them, but nothing on
  //    this side ever does, so the layout autosave would write the pre-sketch
  //    coordinates back. Set directly with the isExternal* skips armed, not via
  //    applyLayoutState — that seeds orientation from the outgoing order.
  // 3. Clear the undo history, which is keyed on user._id and so survives an
  //    apply; Ctrl+Z would restore the old pin map onto the new graph.
  // 4. Refetch: the apply response carries counts, not rows.
  const refreshAfterSketchApply = React.useCallback(async () => {
    setMemberRecords([]);

    chartBoxRef.current?.invalidateLayout?.();
    isExternalPositionUpdate.current = true;
    setCustomPositions(new Map());
    isExternalOrderUpdate.current = true;
    setEstablishedOrder(new Map());
    isExternalOrderLockedUpdate.current = true;
    setOrderLocked(false);

    historyClear();

    // Same three the save path invalidates after a write.
    queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
    queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });

    const { data } = await axios.get(
      APICONSTANTS.getPedigreeDetails.replace(':familyId', user.familyId)
    );
    // No `diagnoses` key on purpose: the reducer recomputes diagnoses,
    // phenotypes and genotypes from the family array when it is omitted, which
    // is how the sketch's newly coded conditions reach the legend.
    dispatchUpdate({
      type: EVENTS.SET_PEDIGREE_SELECTED_USER,
      value: {
        family: data.families,
        selectedUser: data.proband,
        perf: data.perf
      }
    });
  }, [queryClient, dispatchUpdate, historyClear, user.familyId]);

  return (
    <Box>
      {/* Hidden export elements — captured by ExportMenu via html2canvas(getElementById(...)),
          which renders the element by reference regardless of where it sits. Positioned
          OFF-SCREEN (not merely z-index:-1) so they are never revealed on the canvas: while
          the worker computes the layout the SVG isn't painted yet, and a z-index:-1 element
          with nothing covering it would flash at the top-left until the chart appears. */}
      <div
        id="pedigree-export-logo"
        style={{
          position: 'absolute',
          top: 0,
          left: -100000,
          backgroundColor: 'white',
          padding: 12
        }}
      >
        <img
          src={theme.logoURL && theme.logoName ? theme.logoURL : trakgeneLogo}
          alt={t('Logo')}
          style={{ height: 50 }}
          crossOrigin="anonymous"
          onError={refreshThemeAssets}
        />
      </div>
      {/* TGCLOUD-1759 — the identifier slot on the exported pedigree. ExportMenu
          has looked up `#pedigree-identifier` since it was written and reserves a
          header slot for it (top-left, under the logo), but no element ever
          carried the id, so the lookup always returned null and the slot has
          never rendered.

          It shows `headlineId`, which is the identifier leading the on-screen
          chart header: the G number, or the primary external ID when the group's
          display-label mode selects it (TGCLOUD-1663), and always the G number on
          an anonymous-pedigrees deployment because `useExternalPrimary` is
          clamped off there. Deriving it from the same value rather than picking
          one here is the point — an exported clinical document that disagreed
          with the chart it was exported from would be worse than the blank slot
          this replaces. Secondary "also known as" IDs are deliberately left to
          the on-screen header.

          Rendered off-screen for the reason in the comment above, and on white
          with explicit dark text: the export canvas is a document, so it must not
          follow a dark UI theme the way an on-screen panel does. */}
      {headlineId && (
        <div
          id="pedigree-identifier"
          style={{
            position: 'absolute',
            top: 0,
            left: -100000,
            backgroundColor: 'white',
            padding: 12,
            color: '#1a1a1a',
            fontSize: 18,
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}
        >
          {headlineId}
        </div>
      )}
      {/* TGCLOUD-1791 — the family note in the export header, under the
          identifier. Same off-screen-capture pattern as #pedigree-identifier
          above, which is why it is here rather than drawn into the SVG: the
          label-background post-pass is scoped to node groups, so chart-level
          SVG text gets no backing rect and relationship lines and the dot grid
          would show through it.

          `maxWidth` because unlike the identifier this is a paragraph, and
          exportLayout sizes the canvas from the wider of the SVG and the header
          — an unwrapped note would stretch the whole document to its length.
          White with explicit dark text: the export is a document and must not
          follow a dark UI theme. */}
      {showFamilyNoteOnChart && (
        <div
          id="pedigree-family-note"
          style={{
            position: 'absolute',
            top: 0,
            left: -100000,
            backgroundColor: 'white',
            padding: 12,
            color: '#1a1a1a',
            fontSize: 14,
            maxWidth: 900,
            whiteSpace: 'pre-wrap'
          }}
        >
          {familyNoteText}
        </div>
      )}
      <div
        id="diagnoses-legend"
        style={{
          position: 'absolute',
          top: 0,
          left: -100000,
          width: 320,
          backgroundColor: 'white',
          padding: 12
        }}
      >
        {labelsSet.showRecordStatus &&
          (hasPrimaryRecord || hasSecondaryRecord) && (
            <div
              style={{
                display: 'flex',
                gap: 15,
                flexWrap: 'wrap',
                marginBottom: 8
              }}
            >
              {hasPrimaryRecord && (
                <RecordBadgeSwatch
                  role="primary"
                  teal={theme.accent || '#2ba9bf'}
                  label={t('Primary record')}
                />
              )}
              {hasSecondaryRecord && (
                <RecordBadgeSwatch
                  role="secondary"
                  teal={theme.accent || '#2ba9bf'}
                  label={t('Secondary record (read-only)')}
                />
              )}
            </div>
          )}
        {labelsSet.diseases && users.diagnoses.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Diseases')}
            </Typography>
            <LegendMenu
              dataset={memberRecords}
              diagnoses={familyDiseases}
              id="diagnoses-legend-export"
              showLegendTxt
              setDataset={setMemberRecords}
              draggable={false}
              labelsSet={labelsSet}
              carrierPresentStates={displayCarrier.presentStatesByDisease}
              showCarrierMarks={labelsSet?.showCarrierMarks !== false}
              carrierStyle={carrierStyle.activeStyle}
              showRecordLegend={false}
            />
          </div>
        )}
        {users.phenotypes.length > 0 && labelsSet.phenotypes !== false && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Phenotypes')}
            </Typography>
            <PhenotypesLegend
              phenotypes={users.phenotypes}
              id="phenotypes-legend-export"
              dataset={memberRecords}
              setDataset={setMemberRecords}
              labelsSet={labelsSet}
              draggable={false}
            />
          </div>
        )}
        {users.genotypes.length > 0 && labelsSet.genotypes !== false && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Genotypes')}
            </Typography>
            <GenotypesLegend
              genotypes={users.genotypes}
              id="genotypes-legend-export"
              dataset={memberRecords}
              setDataset={setMemberRecords}
              labelsSet={labelsSet}
              draggable={false}
            />
          </div>
        )}
        <VariantPathogenicityLegend members={memberRecords} />
      </div>
      <RightContainer
        ref={familyCardRef}
        // Click-through, but opaque: a legend drag must not treat the chart
        // hidden behind this card as a drop target (useLegendNodeDrop).
        data-legend-drop-shield=""
        style={{
          pointerEvents: 'none',
          flexDirection: 'column',
          // The family card and the lock banner are canvas chrome: positioned
          // over the working area and offset against the drawer's 400px. On a
          // person tab there is no canvas for them to annotate, and before this
          // the card was the ONLY thing still painting there, floating over an
          // empty region (TGCLOUD-1669 Phase 7). `preview` already hid them for
          // the same reason.
          ...(preview || personMemberId ? { display: 'none' } : {}),
          ...(sidebarState.expand
            ? { left: 12, right: 'auto' }
            : {
                right: sidebarState.open ? 412 : 28,
                borderRadius: 8,
                borderRight: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              })
        }}
      >
        {/* Heading */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            margin: '-12px -16px 0',
            padding: '10px 16px',
            backgroundColor: '#f5f6f8',
            borderRadius: '8px 8px 0 0'
          }}
        >
          {/* pointerEvents re-enabled on the headline itself: the wrapper
              disables them so clicks fall through to the chart, which also
              stopped the `title` tooltip that a truncated identifier relies on. */}
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              pointerEvents: 'none',
              '& > .MuiTypography-root': { pointerEvents: 'auto' }
            }}
          >
            <Typography
              title={headlineId}
              sx={{
                color: theme.header,
                fontWeight: 700,
                fontSize: '1.2rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {headlineId}
            </Typography>

            {/* TGCLOUD-1791 — the family note, on screen, under the identifier.
                Clamped to three lines with the full text on hover: this header
                floats OVER the chart, so an unbounded note would cover the top
                generations. The export copy (#pedigree-family-note) is not
                clamped — a document should carry the whole note. */}
            {showFamilyNoteOnChart && (
              <Typography
                title={familyNoteText}
                sx={{
                  mt: 0.5,
                  pointerEvents: 'auto',
                  fontSize: '0.78rem',
                  lineHeight: 1.35,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {familyNoteText}
              </Typography>
            )}

            {useExternalPrimary && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mt: 0.5,
                  pointerEvents: 'auto'
                }}
              >
                <Chip
                  label={headerTitle}
                  size="small"
                  title={headerTitle}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(43, 169, 191, 0.15)',
                    color: '#1a7a8a',
                    maxWidth: 160,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
                <Typography
                  sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                >
                  {t('Master ID')}
                </Typography>
                {secondaryIds.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    size="small"
                    title={id}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary',
                      maxWidth: 140,
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                ))}
              </Box>
            )}

            {!anonymousActive &&
              !useExternalPrimary &&
              externalFamilyIds.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5,
                    pointerEvents: 'auto'
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                  >
                    {t('Also known as')}
                  </Typography>
                  {externalFamilyIds.map((id, idx) => (
                    <Chip
                      key={id}
                      label={id}
                      size="small"
                      title={id}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: idx === 0 ? 600 : 500,
                        backgroundColor:
                          idx === 0
                            ? 'rgba(43, 169, 191, 0.15)'
                            : 'rgba(0,0,0,0.06)',
                        color: idx === 0 ? '#1a7a8a' : 'text.secondary',
                        maxWidth: 160,
                        '& .MuiChip-label': { px: 0.75 }
                      }}
                    />
                  ))}
                </Box>
              )}
          </Box>
          {/* Collapse lives on row 1 beside the identifier; it acts on the
              panel, not on the chart, and it keeps row 2 for the tools. */}
          <Box sx={{ pointerEvents: 'auto', flexShrink: 0 }}>
            <Tooltip
              title={legendCollapsed ? t('Expand panel') : t('Collapse panel')}
            >
              <IconButton
                size="small"
                onClick={() => setLegendCollapsed((prev) => !prev)}
              >
                {legendCollapsed ? (
                  <UnfoldMore fontSize="small" sx={{ color: theme.accent }} />
                ) : (
                  <UnfoldLess fontSize="small" sx={{ color: theme.accent }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Tools, on their own row (TGCLOUD-1663 §11.3). They shared one flex
            row with the identifier and refused to shrink, so a nine-icon strip
            took the whole width and the G number was ellipsed to "G-11-…" — and
            the identifier has to stay readable on a pedigree at all times.
            Widening the card would have taken that space from the chart
            instead, which is worse with the record sidebar open. Order is
            clinical-workflow first: the record, how it is labelled, bulk edit,
            cascade, then the view-level tools. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            pointerEvents: 'auto',
            mx: '-16px',
            mb: 0.5,
            px: '10px',
            // Symmetric, so the icons sit centred in their own strip. The row
            // used to carry a bottom padding only and inherit the header band's
            // 6px bottom margin above it, which read as 16px above the icons
            // against 4px below.
            py: '5px',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Tooltip title={t('Family record')}>
            <IconButton
              size="small"
              onClick={() => chartBoxRef.current?.openFamilyRecord()}
            >
              <PeopleAlt fontSize="small" sx={{ color: theme.accent }} />
            </IconButton>
          </Tooltip>
          {/* TGCLOUD-1663 — the single entry point for label configuration.
              It sits here, next to the other view controls, because labels are
              a display setting: reaching them through "Family record" was the
              reason nobody could find them. */}
          <Tooltip title={t('Pedigree labels')}>
            <IconButton
              size="small"
              data-va-anchor="pedigree.header.labels"
              onClick={() => setLabelModalOpen(true)}
            >
              <LabelIcon fontSize="small" sx={{ color: theme.accent }} />
            </IconButton>
          </Tooltip>
          {!isLocked && (
            <Tooltip title={t('Bulk edit family members')}>
              <IconButton
                size="small"
                data-va-anchor="pedigree.header.bulkEdit"
                onClick={() => chartBoxRef.current?.openBulkEdit()}
              >
                <PlaylistAdd fontSize="small" sx={{ color: theme.accent }} />
              </IconButton>
            </Tooltip>
          )}
          {!isLocked && analysis?.family && (
            <CascadeScreeningButton
              analysis={analysis}
              diagnoses={familyDiseases}
              labelsSet={labelsSet}
              members={standardVisibleMembers}
              accent={theme.accent}
              onApply={(pids) => chartBoxRef.current?.applySelection(pids)}
            />
          )}
          <OverlaysMenu
            labelsSet={labelsSet}
            setLabelsSet={setLabelsSet}
            analysis={analysis}
            accent={theme.accent}
            slots={dataOverlays.slots}
            treatment={dataOverlays.treatment}
            onTreatmentChange={dataOverlays.setTreatment}
            onAddSlot={dataOverlays.addSlot}
            onRemoveSlot={dataOverlays.removeSlot}
            onToggleSlot={dataOverlays.toggleSlot}
            onUpdateSlot={dataOverlays.updateSlot}
            onOpen={dataOverlays.activate}
            openSignal={overlaysOpenSignal}
            facts={dataOverlays.facts}
            factsLoading={dataOverlays.factsLoading}
            factsError={dataOverlays.factsError}
            unavailable={dataOverlays.unavailable}
            personIndex={dataOverlays.personIndex}
            asOf={dataOverlays.asOf}
            fmtDate={dataOverlays.fmtDate}
            memberRecords={memberRecords}
            diagnoses={users?.diagnoses}
          />
          {viewsEnabled && (
            <ViewsMenu
              views={views}
              activeView={activeView}
              userType={loggedInUser?.userType}
              isLocked={isLocked}
              accent={theme.accent}
              onApplyView={handleApplyView}
              onApplyStandard={handleApplyStandard}
              onSaveAs={handleSaveAsView}
              onRename={handleRenameView}
              onShare={handleShareView}
              onDelete={handleDeleteView}
              onUpdateLayout={handleUpdateViewLayout}
            />
          )}
          {/* Read-only, so deliberately NOT gated on isLocked — the history
              is most needed when the pedigree is locked after a bad edit. */}
          {user?.familyId && (
            <Tooltip title={t('Pedigree history')}>
              <IconButton size="small" onClick={() => setHistoryOpen(true)}>
                <History fontSize="small" sx={{ color: theme.accent }} />
              </IconButton>
            </Tooltip>
          )}
          {/* TGCLOUD-1674 — legend visibility used to be an accordion in the
              record sidebar. Configuring it belongs next to the legend it
              configures; tick boxes ON the legend were considered and
              rejected on appearance, so this opens a modal. */}
          <Tooltip title={t('Choose what the legend shows')}>
            <IconButton
              size="small"
              data-va-anchor="pedigree.header.legendConfig"
              onClick={() => setLegendModalOpen(true)}
            >
              <TuneIcon fontSize="small" sx={{ color: theme.accent }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* TGCLOUD-1670 — what the active data overlays actually found. An HTML
            row inside the family card, deliberately not a bar floating over the
            canvas: the canvas is the exported artefact, and a floating bar
            covers the nodes it is describing. */}
        <DataOverlayResultsBar
          slots={dataOverlays.slots}
          cursors={dataOverlays.cursors}
          fmtDate={dataOverlays.fmtDate}
          onStep={(index, delta) => {
            const pid = dataOverlays.step(index, delta);
            if (pid != null) chartBoxRef.current?.centerOn(pid);
          }}
          onSelect={(index) =>
            chartBoxRef.current?.applySelection(dataOverlays.pidsFor(index))
          }
          onRefresh={dataOverlays.refresh}
          onRemove={dataOverlays.removeSlot}
          onOpenHidden={() => setHiddenPanelOpen(true)}
        />

        {/* Legend Menu + Dates */}
        <Collapse in={!legendCollapsed}>
          <div
            style={{
              pointerEvents: 'auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: 'calc(100vh - 230px)',
              minHeight: 0
            }}
          >
            {labelsSet.showRecordStatus &&
              (hasPrimaryRecord || hasSecondaryRecord) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 15,
                    flexWrap: 'wrap',
                    marginBottom: 8
                  }}
                >
                  {hasPrimaryRecord && (
                    <RecordBadgeSwatch
                      role="primary"
                      teal={theme.accent || '#2ba9bf'}
                      label={t('Primary record')}
                    />
                  )}
                  {hasSecondaryRecord && (
                    <RecordBadgeSwatch
                      role="secondary"
                      teal={theme.accent || '#2ba9bf'}
                      label={t('Secondary record (read-only)')}
                    />
                  )}
                </div>
              )}
            {labelsSet.diseases && (
              <div
                style={{
                  marginBottom: users.diagnoses.length ? '15px' : '0px'
                }}
              >
                {users.diagnoses.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        flexGrow: 1
                      }}
                    >
                      {t('Diseases')}
                    </Typography>
                    {someDiseasesHidden && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={showAllDiseases}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0,
                          px: 0.5,
                          minWidth: 'auto'
                        }}
                      >
                        {t('Show all')}
                      </Button>
                    )}
                  </div>
                ) : null}

                <LegendMenu
                  dataset={memberRecords}
                  diagnoses={familyDiseases}
                  id="diagnoses-legend-canvas"
                  showLegendTxt
                  setDataset={setMemberRecords}
                  draggable={legendDragEnabled}
                  setPedigreeState={setPedigreeState}
                  dropBlocker={legendDropBlocker}
                  labelsSet={labelsSet}
                  carrierPresentStates={displayCarrier.presentStatesByDisease}
                  showCarrierMarks={labelsSet?.showCarrierMarks !== false}
                  carrierStyle={carrierStyle.activeStyle}
                  showRecordLegend={false}
                />
              </div>
            )}

            {/* Phenotypes menu */}
            {users.phenotypes.length > 0 && labelsSet.phenotypes !== false && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      flexGrow: 1
                    }}
                  >
                    {t('Phenotypes')}
                  </Typography>
                  {somePhenotypesHidden && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={showAllPhenotypes}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0,
                        px: 0.5,
                        minWidth: 'auto'
                      }}
                    >
                      {t('Show all')}
                    </Button>
                  )}
                </div>
                <PhenotypesLegend
                  draggable={legendDragEnabled}
                  setPedigreeState={setPedigreeState}
                  dropBlocker={legendDropBlocker}
                  phenotypes={users.phenotypes}
                  dataset={memberRecords}
                  setDataset={setMemberRecords}
                  id="phenotypes-legend-canvas"
                  labelsSet={labelsSet}
                />
              </div>
            )}

            {/* Genotypes menu */}
            {users.genotypes.length > 0 && labelsSet.genotypes !== false && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      flexGrow: 1
                    }}
                  >
                    {t('Genotypes')}
                  </Typography>
                  {someGenotypesHidden && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={showAllGenotypes}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0,
                        px: 0.5,
                        minWidth: 'auto'
                      }}
                    >
                      {t('Show all')}
                    </Button>
                  )}
                </div>
                <GenotypesLegend
                  draggable={legendDragEnabled}
                  setPedigreeState={setPedigreeState}
                  dropBlocker={legendDropBlocker}
                  genotypes={users.genotypes}
                  dataset={memberRecords}
                  setDataset={setMemberRecords}
                  id="genotypes-legend-canvas"
                  labelsSet={labelsSet}
                />
              </div>
            )}

            <VariantPathogenicityLegend members={memberRecords} />

            {/* On-screen only, and INSIDE this scroll div. It sits outside the
                dates Box on purpose — that Box is the one on-screen element the
                export captures, so an overlay row placed inside it would reach
                the PDF — but it belongs with the blocks it visually matches:
                placed after the scroll div it stayed pinned while Diseases,
                Genotypes and Variant Pathogenicity scrolled away above it. */}
            <DataOverlayLegend slots={dataOverlays.slots} />
          </div>

          {/* Pedigree Dates */}
          <Box
            id="pedigree-dates"
            sx={{
              flexShrink: 0,
              mt: 1,
              pt: 1,
              padding: '8px',
              borderTop: '1px solid #e2e8f0',
              pointerEvents: 'none'
            }}
          >
            <PedigreeDates
              dateModified={dateModified}
              theme={theme}
              showPrintDate={false}
            />
          </Box>
        </Collapse>

        <UnrecognisedCarrierStatusNotice members={memberRecords} />

        {hiddenMembers.length > 0 && (
          <Box
            sx={{
              mt: 1,
              pointerEvents: 'auto',
              borderTop: '1px solid #e2e8f0',
              pt: 1
            }}
          >
            {/* Header row — toggle + count */}
            <Box
              onClick={() => setHiddenPanelOpen((prev) => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {t('Hidden Members ({{count}})', {
                  count: hiddenMembers.length
                })}
              </Typography>
              {hiddenPanelOpen ? (
                <ExpandLess fontSize="small" sx={{ opacity: 0.6 }} />
              ) : (
                <ExpandMore fontSize="small" sx={{ opacity: 0.6 }} />
              )}
            </Box>

            <Collapse in={hiddenPanelOpen}>
              {/* Action row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  mt: 0.75,
                  mb: 0.5
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', py: 0.25, flex: 1 }}
                  onClick={unhideAll}
                >
                  {t('Unhide All')}
                </Button>
                {selectedHiddenPids.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    sx={{ fontSize: '0.7rem', py: 0.25, flex: 1 }}
                    onClick={unhideSelected}
                  >
                    {t('Unhide ({{count}})', {
                      count: selectedHiddenPids.length
                    })}
                  </Button>
                )}
              </Box>

              {/* Member list */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 160,
                  overflowY: 'auto'
                }}
              >
                {hiddenMembers.map((m) => {
                  const isSelected = selectedHiddenPids.includes(m.pid);
                  return (
                    <Box
                      key={m.pid}
                      onClick={() => toggleHiddenSelection(m.pid)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        px: 0.5,
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleHiddenSelection(m.pid)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {[m.firstName, m.lastName].filter(Boolean).join(' ') ||
                          m.upn ||
                          m.pid}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        )}
      </RightContainer>

      {!preview && !personMemberId && (
        <Box
          sx={{ position: 'fixed', zIndex: 9, width: RIGHT_PANEL_WIDTH }}
          style={{
            top: familyCardBottom != null ? familyCardBottom + 8 : 0,
            // Only ever set 'hidden', never 'visible'. `visibility` is the one
            // property a descendant can use to un-hide itself against an
            // ancestor, and the multi-tab workspace hides INACTIVE tabs with
            // visibility:hidden (deliberately, so the canvas keeps its layout
            // box). An explicit 'visible' here made every background tab's
            // banner float over the active chart at the same fixed coordinates,
            // with a live Review button opening the wrong family's FHQ.
            ...(familyCardBottom == null ? { visibility: 'hidden' } : {}),
            ...(sidebarState.expand
              ? { left: 12, right: 'auto' }
              : { right: sidebarState.open ? 412 : 28 })
          }}
        >
          <FhqReviewBanner
            probandId={user?._id}
            patient={patient}
            enabled={
              content?.settings?.systemFeatures
                ?.FEATURE_FAMILY_HISTORY_QUESTIONNAIRE !== false
            }
            onReview={() => chartBoxRef.current?.openFamilyHistory()}
          />
        </Box>
      )}

      {/* Pedigree sketch launcher. Hidden in the same three cases the legend
          box is — FHQ preview, a PERSON tab (no canvas there), and a locked
          pedigree — plus the patient portal, the admin toggle, and anyone the
          sketch's routes refuse (a student, a read-only security group), so
          the panel is never offered when every request it makes would come
          back 403. */}
      {showSketchLauncher({
        preview,
        patient,
        personMemberId,
        isLocked,
        sketchEnabled: vaFeatureFlags?.pedigreeSketchEnabled,
        userType: loggedInUser?.userType,
        canWritePedigrees: canWriteModule('pedigrees')
      }) && (
        <PedigreeAIGenerator
          accent={theme.accent}
          anchor={
            sidebarState.expand
              ? { left: 12, right: 'auto' }
              : { right: sidebarState.open ? 412 : 28 }
          }
          onApply={async (draft, opts) => {
            // Errors propagate to the panel, which offers to replace when
            // the family already has members.
            const res = await applyPedigreeConfig(user._id, draft.config, opts);
            // Awaited so a refresh failure surfaces in the panel rather than
            // leaving it closed over a canvas showing the old family.
            await refreshAfterSketchApply();
            setSnack({
              status: true,
              msg: t(
                'Built a {{people}}-person pedigree ({{created}} members created).',
                { people: draft.people, created: res.created }
              ),
              severity: 'success'
            });
          }}
        />
      )}

      {historyOpen && (
        <CustomDialog
          title="Pedigree History"
          open={historyOpen}
          handleClose={() => setHistoryOpen(false)}
          minHeight="auto"
          maxWidth="lg"
          fullWidth
        >
          <ModificationHistoryModal
            modificationType={CONSTANTS.auditLogType.pedigreeChart}
            familyId={user?.familyId}
          />
        </CustomDialog>
      )}
      {labelModalOpen && (
        <LabelPresetModal
          open={labelModalOpen}
          handleClose={() => setLabelModalOpen(false)}
          labelsSet={labelsSet}
          setLabelsSet={setLabelsSet}
          scoped={labelPresets}
          reloadPresets={reloadPresets}
          pedigreeSetting={content?.settings?.pedigreeSetting}
          appliedPresetId={appliedPresetId}
          setAppliedPresetId={setAppliedPresetId}
          // The Display label section moved in from the sidebar (TGCLOUD-1674);
          // this component already holds the family identifiers it previews.
          gNumber={user?.familyId || ''}
          primaryExternalId={externalFamilyIds[0] || ''}
          hasExternalIds={externalFamilyIds.length > 0}
          diagnoses={familyDiseases}
          probandPid={getProbandState(memberRecords).probandPid}
        />
      )}
      {legendModalOpen && (
        <LegendConfigModal
          open={legendModalOpen}
          handleClose={() => setLegendModalOpen(false)}
          labelsSet={labelsSet}
          setLabelsSet={setLabelsSet}
          users={users}
          diagnoses={familyDiseases}
          colourKeyEnabled={colourKeyEnabled}
          memberRecords={memberRecords}
          setMemberRecords={setMemberRecords}
          carrierPresentStates={displayCarrier.presentStatesByDisease}
          showCarrierMarks={labelsSet?.showCarrierMarks !== false}
          carrierStyle={carrierStyle.activeStyle}
        />
      )}
      <ChartContexts
        structuralEdit={structuralEditContext}
        memberRowSync={memberRowSync}
      >
        <ChartBox
          personMemberId={personMemberId}
          initialViewState={initialViewState}
          onPersonLockStateChange={onPersonLockStateChange}
          onUnsavedSectionsChange={onUnsavedSectionsChange}
          ref={chartBoxRef}
          onOpenLegendConfig={() => setLegendModalOpen(true)}
          pedRef={pedRef}
          user={user}
          family={family}
          options={options}
          setPedigreeState={setPedigreeState}
          isSaving={pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS}
          // The canvas indicator must not show its green tick after a write the
          // server refused (or one that was never sent): `isSaving` going false is
          // all PedigreeSaveIndicator needs to flash "Saved just now".
          saveFailed={saveFailed}
          colors={colors}
          memberRecords={memberRecords}
          canvasMembers={canvasMembers}
          carrier={displayCarrier}
          // TGCLOUD-1793 (1504 B4). The canonical disease -> colour map, taken
          // from the ALWAYS-computed `carrier` above, NOT from `displayCarrier`:
          // that is EMPTY_CARRIER whenever the carrier-status flag is off, which
          // is the shipped default, so sourcing the fill from it would paint
          // every affected segment as the node background.
          diseaseColorMap={carrier.diseaseColorMap}
          carrierStyle={carrierStyle}
          analysis={analysis}
          setMemberRecords={setMemberRecords}
          labelsSet={labelsSet}
          setLabelsSet={setLabelsSet}
          customPositions={customPositions}
          setCustomPositions={setCustomPositions}
          establishedOrder={establishedOrder}
          orderLocked={orderLocked}
          setOrderLocked={setOrderLocked}
          setEstablishedOrder={setEstablishedOrder}
          setOptions={setOptions}
          isLocked={isLocked}
          isMine={isMine}
          lockState={lockState}
          requestStatus={requestStatus}
          onRequestLock={onRequestLock}
          onCancelRequest={onCancelRequest}
          onForceAcquireLock={onForceAcquireLock}
          pendingRequest={pendingRequest}
          onRespondToRequest={onRespondToRequest}
          personLockHook={personLockHook}
          isMigrated={isMigrated}
          onSidebarChange={setSidebarState}
          linkStatusMap={linkStatusMap}
          refetchLinkStatus={refetchLinkStatus}
          onHistoryCheckpoint={
            layoutConfig.ENABLE_UNDO_REDO ? historyCheckpoint : undefined
          }
          onAutomaticLayoutWrite={noteAutomaticLayoutWrite}
          onHistoryUndo={handleHistoryUndo}
          onHistoryRedo={handleHistoryRedo}
          canUndo={undoRedoActive && historyCanUndo}
          canRedo={undoRedoActive && historyCanRedo}
          viewHide={viewHide}
          onOpenOverlays={() => setOverlaysOpenSignal((n) => n + 1)}
          onStepOverlayMatch={(delta) => {
            // The first enabled slot is the one J and K walk. With two overlays on
            // the clinician steps the one they added first, and the results row
            // arrows remain the way to walk the other.
            const index = dataOverlays.slots.findIndex(
              (s) => s.enabled && s.results
            );
            if (index < 0) return;
            const pid = dataOverlays.step(index, delta);
            if (pid != null) chartBoxRef.current?.centerOn(pid);
          }}
          dataOverlaySlotsRef={dataOverlays.slotsRef}
          dataOverlayStyleRef={dataOverlays.styleRef}
          dataOverlayVersion={dataOverlays.version}
        />
      </ChartContexts>
    </Box>
  );
}

export default Pedigree;
